import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Loader2, Database, CheckCircle2, AlertTriangle, Package, Wrench, ArrowRightLeft, Search, GitFork, ShieldCheck, FileInput, PackageCheck, Trash2, RotateCcw, RefreshCw, Mail } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import api from '@/lib/api';
import { subcontractAPI, dispatchAPI, inventoryAPI } from '@/lib/api';

function BackfillMrbTraceLotsCard() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const res = await api.post('/mrb/admin/backfill-trace-lots');
      setResult(res.data);
      toast.success(`Backfilled ${res.data.backfilled_count} trace lot(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Backfill failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-muted-foreground">
        Creates <code>trace_lots</code> for legacy MRB cases that lack one but carry WO/customer info. Useful for old quarantine→rejection rows where dispatch lines now show empty trace fields. Idempotent — only fills missing lots.
      </p>
      <Button size="sm" onClick={run} disabled={loading} data-testid="backfill-mrb-trace-btn">
        {loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <PackageCheck className="w-3.5 h-3.5 mr-1" />}
        {loading ? 'Backfilling...' : 'Backfill MRB Trace Lots'}
      </Button>
      {result && (
        <div className="text-[11px] space-y-1 border rounded p-2">
          <div>Scanned: <b>{result.scanned}</b> · Backfilled: <b className="text-emerald-700">{result.backfilled_count}</b> · Skipped: <b className="text-amber-700">{result.skipped_count}</b> · Failed: <b className="text-rose-700">{result.failed_count}</b></div>
          {result.backfilled?.length > 0 && (
            <div className="max-h-40 overflow-auto mt-1 text-[10px]">
              {result.backfilled.map((b, i) => (
                <div key={i} className="flex items-center justify-between py-0.5 border-b last:border-0">
                  <span className="truncate">{b.trace_id} · {b.wo_number} · {b.customer_wo_number || '—'}</span>
                  <Badge variant="outline" className="text-[9px]">{b.godown} · {b.qty}</Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BackfillInstrumentItemMasterCard() {
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [info, setInfo] = useState(null);
  const [result, setResult] = useState(null);

  const check = async () => {
    setChecking(true);
    setResult(null);
    try {
      const res = await api.get('/tools/unlinked-count');
      setInfo(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Check failed');
    } finally { setChecking(false); }
  };

  const run = async () => {
    setLoading(true);
    try {
      const res = await api.post('/tools/backfill-item-master');
      setResult(res.data);
      toast.success(res.data.message || `Created ${res.data.created} item(s)`);
      setInfo(null);
      check();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Backfill failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-muted-foreground">
        Finds instruments that were created directly (not linked to the Item Master) and, on your confirmation, creates a matching <b>Fixed Asset</b> (Calibration Required) for each and links it back. Instruments must now originate from the Item Master.
      </p>
      <Button size="sm" variant="outline" onClick={check} disabled={checking} data-testid="check-unlinked-instruments-btn">
        {checking ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Search className="w-3.5 h-3.5 mr-1" />}
        {checking ? 'Checking...' : 'Check unlinked instruments'}
      </Button>
      {info && (
        <div className="text-[11px] space-y-2 border rounded p-2">
          <div>Unlinked instruments: <b className={info.count > 0 ? 'text-amber-700' : 'text-emerald-700'}>{info.count}</b></div>
          {info.count > 0 && (
            <>
              <div className="max-h-40 overflow-auto text-[10px]">
                {info.instruments.map((t, i) => (
                  <div key={i} className="flex items-center justify-between py-0.5 border-b last:border-0">
                    <span className="truncate">{t.instrument_name}</span>
                    <Badge variant="outline" className="text-[9px]">{t.instrument_code || t.serial_number || '—'}</Badge>
                  </div>
                ))}
              </div>
              <Button size="sm" onClick={run} disabled={loading} className="bg-[#0F172A] hover:bg-[#1E293B] text-white" data-testid="backfill-instruments-btn">
                {loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <PackageCheck className="w-3.5 h-3.5 mr-1" />}
                {loading ? 'Creating...' : `Add ${info.count} to Item Master`}
              </Button>
            </>
          )}
          {info.count === 0 && <div className="text-emerald-700">All instruments are linked to the Item Master. ✓</div>}
        </div>
      )}
      {result && (
        <div className="text-[11px] border rounded p-2">Created <b className="text-emerald-700">{result.created}</b> Item Master entrie(s).</div>
      )}
    </div>
  );
}


function MigrateInvoicesCard() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const res = await api.post('/dispatch/challans/migrate-invoices-to-storage');
      setResult(res.data);
      toast.success(`Migrated ${res.data.migrated_count} invoice(s) to object storage`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Migration failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-muted-foreground">
        Backfills all legacy dispatch invoice attachments from local disk (<code>/app/backend/uploads/invoices</code>) into Emergent Object Storage so they survive production deployments. Safe to re-run — idempotent.
      </p>
      <Button size="sm" onClick={run} disabled={loading} data-testid="migrate-invoices-btn">
        {loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileInput className="w-3.5 h-3.5 mr-1" />}
        {loading ? 'Migrating...' : 'Migrate Invoices → Storage'}
      </Button>
      {result && (
        <div className="text-[11px] space-y-1 border rounded p-2">
          <div>Scanned: <b>{result.scanned}</b> · Migrated: <b className="text-emerald-700">{result.migrated_count}</b> · Missing on disk: <b className="text-amber-700">{result.missing_on_disk_count}</b> · Failed: <b className="text-rose-700">{result.failed_count}</b></div>
          {result.missing_on_disk_count > 0 && (
            <p className="text-[10px] text-amber-700">These files were already lost in prior deployments — users will need to re-upload them via the Reconcile dialog.</p>
          )}
        </div>
      )}
    </div>
  );
}

function HealStaleIndentsCard() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const res = await api.post('/purchase/auto-indent/heal-stale');
      setResult(res.data);
      toast.success(`Healed ${res.data.healed_count} stale indent(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Heal failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-3">
      <p className="text-[11px] text-muted-foreground">
        Finds <b>purchase indents</b> stuck in <code>po_sent</code>/<code>approved</code>/<code>purchase_request</code> whose PO was already fully received or closed. Marks them as <code>received</code> so auto-indent can re-trigger on future shortages.
      </p>
      <Button size="sm" onClick={run} disabled={loading} data-testid="heal-stale-indents-btn">
        {loading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <PackageCheck className="w-3.5 h-3.5 mr-1" />}
        {loading ? 'Scanning...' : 'Scan & Heal Stale Indents'}
      </Button>
      {result && (
        <div className="text-[11px] space-y-1">
          <div><b>Healed:</b> {result.healed_count}</div>
          {result.healed?.length > 0 && (
            <div className="max-h-48 overflow-auto border rounded p-2">
              {result.healed.map(h => (
                <div key={h.id} className="flex items-center justify-between py-0.5 border-b last:border-0">
                  <span className="truncate">{h.item_name}</span>
                  <Badge variant="outline" className="text-[9px]">was: {h.was_status}</Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function ReceiptQCFixer() {
  const [itemCode, setItemCode] = useState('');
  const [searching, setSearching] = useState(false);
  const [result, setResult] = useState(null);
  const [fixing, setFixing] = useState({});

  const searchItem = async () => {
    if (!itemCode.trim()) { toast.error('Enter an item code'); return; }
    setSearching(true);
    setResult(null);
    try {
      const res = await api.post(`/quality/backfill-receipt-qc?item_unique_id=${encodeURIComponent(itemCode.trim())}`);
      setResult(res.data);
      if (res.data.fixable_count === 0 && res.data.transactions?.length > 0) {
        toast.info('All receipts already have QC flags');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Item not found'); }
    finally { setSearching(false); }
  };

  const fixTransaction = async (txnId) => {
    setFixing(f => ({ ...f, [txnId]: true }));
    try {
      const res = await api.post(`/quality/backfill-receipt-qc?transaction_id=${txnId}`);
      toast.success(res.data.message);
      // Refresh
      const refresh = await api.post(`/quality/backfill-receipt-qc?item_unique_id=${encodeURIComponent(itemCode.trim())}`);
      setResult(refresh.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to fix'); }
    finally { setFixing(f => ({ ...f, [txnId]: false })); }
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-xs">Item Code (Unique ID)</Label>
        <div className="flex gap-2 mt-1">
          <Input
            placeholder="Enter item unique code..."
            value={itemCode}
            onChange={e => setItemCode(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && searchItem()}
            className="h-8 text-sm max-w-sm font-mono"
            data-testid="receipt-qc-item-search"
          />
          <Button size="sm" onClick={searchItem} disabled={searching} className="h-8" data-testid="receipt-qc-search-btn">
            {searching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3 mr-1" />}
            Search
          </Button>
        </div>
      </div>

      {result && (
        <div className="space-y-3">
          <div className="p-2 bg-blue-50 rounded border border-blue-200">
            <p className="text-xs font-medium text-blue-800">
              {result.item?.item_name} <span className="font-mono text-[10px] ml-1">({result.item?.unique_id})</span>
            </p>
            <p className="text-[10px] text-blue-600">
              QC on Receipt: {result.item?.perform_qc_on_receipt ? 'Yes' : 'No'} | 
              Total Receipts: {result.transactions?.length || 0} | 
              Need Fix: {result.fixable_count}
            </p>
          </div>

          {result.transactions?.length > 0 ? (
            <table className="w-full text-xs border rounded">
              <thead>
                <tr className="bg-muted/50 border-b">
                  <th className="text-left p-2 font-medium">Reference</th>
                  <th className="text-center p-2 font-medium">Qty</th>
                  <th className="text-left p-2 font-medium">Godown</th>
                  <th className="text-left p-2 font-medium">Date</th>
                  <th className="text-center p-2 font-medium">QC Status</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {result.transactions.map(t => (
                  <tr key={t.id} className={`border-b ${t.needs_fix ? 'bg-amber-50/50' : ''}`}>
                    <td className="p-2 font-mono">{t.reference_number}</td>
                    <td className="p-2 text-center font-medium">{t.quantity}</td>
                    <td className="p-2">{t.godown_name}</td>
                    <td className="p-2 text-muted-foreground">{t.date}</td>
                    <td className="p-2 text-center">
                      {t.qc_required ? (
                        <Badge variant="outline" className="text-[9px] border-emerald-300 text-emerald-700">{t.qc_status}</Badge>
                      ) : (
                        <Badge variant="outline" className="text-[9px] border-amber-300 text-amber-700">No QC</Badge>
                      )}
                    </td>
                    <td className="p-2 text-right">
                      {t.needs_fix ? (
                        <Button size="sm" variant="outline" className="h-6 text-[10px] border-blue-300 text-blue-700 hover:bg-blue-50"
                          onClick={() => fixTransaction(t.id)}
                          disabled={fixing[t.id]}
                          data-testid={`fix-qc-${t.id}`}
                        >
                          {fixing[t.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3 mr-0.5" />}
                          Fix
                        </Button>
                      ) : (
                        <span className="text-[10px] text-emerald-600">OK</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="text-xs text-muted-foreground">No receipt transactions found for this item.</p>
          )}
        </div>
      )}
    </div>
  );
}

function CustomerRefBackfill() {
  const [search, setSearch] = useState('');
  const [onlyMissing, setOnlyMissing] = useState(true);
  const [loading, setLoading] = useState(false);
  const [challans, setChallans] = useState([]);
  const [edits, setEdits] = useState({}); // id -> { customer_challan_number, purchase_order_number }
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await subcontractAPI.listMissingCustomerRefs({ search: search.trim(), only_missing: onlyMissing, limit: 200 });
      setChallans(res.data.challans || []);
      setEdits({});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load challans');
    } finally {
      setLoading(false);
    }
  };

  const setField = (id, field, value) => {
    setEdits(prev => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const save = async (c) => {
    const e = edits[c.id] || {};
    const payload = {};
    if (e.customer_challan_number !== undefined) payload.customer_challan_number = e.customer_challan_number;
    if (e.purchase_order_number !== undefined) payload.purchase_order_number = e.purchase_order_number;
    if (Object.keys(payload).length === 0) { toast.info('Nothing changed'); return; }
    setSavingId(c.id);
    try {
      const res = await subcontractAPI.updateChallanCustomerRefs(c.id, payload);
      const updated = res.data.challan || {};
      setChallans(prev => prev.map(x => x.id === c.id ? { ...x, ...updated } : x));
      setEdits(prev => { const n = { ...prev }; delete n[c.id]; return n; });
      toast.success(`${c.challan_number}: updated`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2 items-end flex-wrap">
        <div className="flex-1 min-w-[180px]">
          <Label className="text-xs">Search (Challan / Supplier / Cust DC / PO)</Label>
          <Input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()} placeholder="e.g. ABCENGINEE or 10001"
            className="h-8 text-xs" data-testid="backfill-search-input" />
        </div>
        <label className="flex items-center gap-1.5 text-[11px] pb-1.5">
          <input type="checkbox" checked={onlyMissing} onChange={e => setOnlyMissing(e.target.checked)} />
          Only missing refs
        </label>
        <Button size="sm" onClick={load} disabled={loading} className="h-8" data-testid="backfill-load-btn">
          {loading ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Loading...</> : <><Search className="w-3 h-3 mr-1" /> Load</>}
        </Button>
      </div>

      {challans.length > 0 && (
        <div className="border rounded-md overflow-auto max-h-[420px]">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="p-2 text-left font-medium">Inbound Challan</th>
                <th className="p-2 text-left font-medium">Supplier/Customer</th>
                <th className="p-2 text-left font-medium">Customer DC #</th>
                <th className="p-2 text-left font-medium">Customer PO #</th>
                <th className="p-2 text-left font-medium">Status</th>
                <th className="p-2"></th>
              </tr>
            </thead>
            <tbody>
              {challans.map(c => {
                const e = edits[c.id] || {};
                const curDC = e.customer_challan_number !== undefined ? e.customer_challan_number : (c.customer_challan_number || '');
                const curPO = e.purchase_order_number !== undefined ? e.purchase_order_number : (c.purchase_order_number || '');
                const dirty = (e.customer_challan_number !== undefined && e.customer_challan_number !== (c.customer_challan_number || '')) ||
                              (e.purchase_order_number !== undefined && e.purchase_order_number !== (c.purchase_order_number || ''));
                return (
                  <tr key={c.id} className="border-t hover:bg-slate-50/50">
                    <td className="p-2 font-mono text-[11px]">{c.challan_number}</td>
                    <td className="p-2 text-[11px]">{c.supplier_name}</td>
                    <td className="p-2">
                      <Input value={curDC} onChange={ev => setField(c.id, 'customer_challan_number', ev.target.value)}
                        className="h-7 text-[11px]" placeholder="— missing —" data-testid={`cust-dc-input-${c.challan_number}`} />
                    </td>
                    <td className="p-2">
                      <Input value={curPO} onChange={ev => setField(c.id, 'purchase_order_number', ev.target.value)}
                        className="h-7 text-[11px]" placeholder="— missing —" data-testid={`cust-po-input-${c.challan_number}`} />
                    </td>
                    <td className="p-2"><Badge variant="outline" className="text-[10px]">{c.status}</Badge></td>
                    <td className="p-2">
                      <Button size="sm" variant="outline" className="h-7 text-[10px]" disabled={!dirty || savingId === c.id}
                        onClick={() => save(c)} data-testid={`save-backfill-${c.challan_number}`}>
                        {savingId === c.id ? 'Saving...' : 'Save'}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {challans.length === 0 && !loading && (
        <p className="text-xs text-muted-foreground">Click Load to find inbound challans with missing Customer DC# / PO#.</p>
      )}
    </div>
  );
}


function ManifestDcCustomerRefCorrector() {
  const [customerId, setCustomerId] = useState('');
  const [since, setSince] = useState('');
  const [includeManifests, setIncludeManifests] = useState(true);
  const [includeDcs, setIncludeDcs] = useState(true);
  const [mismatchOnly, setMismatchOnly] = useState(true);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState({}); // key -> bool
  const [applying, setApplying] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const rowKey = r => `${r.entity_kind}|${r.entity_id}|${r.item_index}`;

  const load = async () => {
    setLoading(true);
    setLastResult(null);
    try {
      const params = {
        mismatch_only: mismatchOnly,
        include_manifests: includeManifests,
        include_dcs: includeDcs,
      };
      if (customerId.trim()) params.customer_id = customerId.trim();
      if (since.trim()) params.since = since.trim();
      const res = await dispatchAPI.customerRefAudit(params);
      const data = res.data.rows || [];
      setRows(data);
      // Pre-select all mismatched rows
      const sel = {};
      data.filter(r => r.mismatch).forEach(r => { sel[rowKey(r)] = true; });
      setSelected(sel);
      if (data.length === 0) toast.info('No rows found for the selected filters.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Audit failed');
    } finally {
      setLoading(false);
    }
  };

  const toggle = (k) => setSelected(prev => ({ ...prev, [k]: !prev[k] }));
  const selectAll = (val) => {
    const next = {};
    rows.forEach(r => { if (r.mismatch) next[rowKey(r)] = val; });
    setSelected(next);
  };

  const applySelected = async () => {
    const targets = rows
      .filter(r => selected[rowKey(r)])
      .map(r => ({ entity_kind: r.entity_kind, entity_id: r.entity_id, item_index: r.item_index }));
    if (targets.length === 0) { toast.error('Select at least one row to correct'); return; }
    if (!window.confirm(`Apply correction to ${targets.length} line(s)? Affected manifests / DCs will be updated and audit-logged. COCs already issued are unchanged — re-issue them after correction.`)) return;
    setApplying(true);
    try {
      const res = await dispatchAPI.customerRefCorrect({ targets });
      setLastResult(res.data);
      toast.success(`Applied: ${res.data.applied} · Unchanged: ${res.data.unchanged}`);
      // Reload audit so the table refreshes
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Correction failed');
    } finally {
      setApplying(false);
    }
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-end">
        <div className="min-w-[180px]">
          <Label className="text-xs">Customer ID (optional)</Label>
          <Input value={customerId} onChange={e => setCustomerId(e.target.value)} placeholder="Customer UUID"
            className="h-8 text-xs" data-testid="ref-correct-customer" />
        </div>
        <div>
          <Label className="text-xs">Since (ISO date)</Label>
          <Input type="date" value={since} onChange={e => setSince(e.target.value)} className="h-8 text-xs" data-testid="ref-correct-since" />
        </div>
        <label className="flex items-center gap-1.5 text-[11px] pb-1.5">
          <input type="checkbox" checked={mismatchOnly} onChange={e => setMismatchOnly(e.target.checked)} />
          Mismatches only
        </label>
        <label className="flex items-center gap-1.5 text-[11px] pb-1.5">
          <input type="checkbox" checked={includeDcs} onChange={e => setIncludeDcs(e.target.checked)} />
          Dispatch Challans
        </label>
        <label className="flex items-center gap-1.5 text-[11px] pb-1.5">
          <input type="checkbox" checked={includeManifests} onChange={e => setIncludeManifests(e.target.checked)} />
          Manifests
        </label>
        <Button size="sm" onClick={load} disabled={loading} className="h-8" data-testid="ref-correct-load-btn">
          {loading ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Auditing...</> : <><Search className="w-3 h-3 mr-1" /> Audit</>}
        </Button>
      </div>

      {rows.length > 0 && (
        <>
          <div className="flex items-center gap-2 text-[11px]">
            <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => selectAll(true)}>Select all mismatches</Button>
            <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => selectAll(false)}>Clear</Button>
            <span className="text-muted-foreground ml-2">{selectedCount} selected · {rows.filter(r => r.mismatch).length} mismatch(es) found</span>
          </div>

          <div className="border rounded-md overflow-auto max-h-[480px]">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="p-2 w-8"></th>
                  <th className="p-2 text-left font-medium">Type</th>
                  <th className="p-2 text-left font-medium">Number</th>
                  <th className="p-2 text-left font-medium">Customer</th>
                  <th className="p-2 text-left font-medium">Item · Trace ID</th>
                  <th className="p-2 text-left font-medium">Current DC# / PO#</th>
                  <th className="p-2 text-left font-medium">Suggested DC# / PO#</th>
                  <th className="p-2 text-left font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const k = rowKey(r);
                  return (
                    <tr key={k} className={`border-t hover:bg-slate-50/50 ${r.mismatch ? 'bg-amber-50/40' : ''}`}>
                      <td className="p-2 text-center">
                        <input
                          type="checkbox"
                          checked={!!selected[k]}
                          disabled={!r.mismatch}
                          onChange={() => toggle(k)}
                          data-testid={`ref-correct-row-${k}`}
                        />
                      </td>
                      <td className="p-2"><Badge variant="outline" className="text-[10px] uppercase">{r.entity_kind}</Badge></td>
                      <td className="p-2 font-mono text-[11px]">{r.entity_number}</td>
                      <td className="p-2 text-[11px]">{r.customer_name || '—'}</td>
                      <td className="p-2 text-[11px]">
                        <div className="font-medium">{r.item_unique_id || r.item_id?.slice(0, 8)}</div>
                        <div className="text-muted-foreground">{r.traceability_id || <em>no trace</em>}</div>
                      </td>
                      <td className="p-2 text-[11px] whitespace-nowrap">
                        <div>DC: <code className="text-[10px]">{r.current_customer_challan_number || '—'}</code></div>
                        <div>PO: <code className="text-[10px]">{r.current_customer_po_number || '—'}</code></div>
                        {r.current_source === 'parent' && (
                          <Badge variant="outline" className="text-[9px] mt-0.5">from {r.entity_kind === 'dc' ? 'DC' : 'manifest'} header</Badge>
                        )}
                      </td>
                      <td className="p-2 text-[11px] whitespace-nowrap">
                        <div>DC: <code className="text-[10px] text-emerald-700">{r.suggested_customer_challan_number || '—'}</code></div>
                        <div>PO: <code className="text-[10px] text-emerald-700">{r.suggested_customer_po_number || '—'}</code></div>
                      </td>
                      <td className="p-2">
                        {r.mismatch
                          ? <Badge className="bg-amber-100 text-amber-800 text-[10px]">Mismatch</Badge>
                          : <Badge variant="outline" className="text-[10px]">OK</Badge>
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <Button
            size="sm"
            onClick={applySelected}
            disabled={applying || selectedCount === 0}
            data-testid="ref-correct-apply-btn"
          >
            {applying ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Applying...</> : <><CheckCircle2 className="w-3 h-3 mr-1" /> Apply correction to {selectedCount} line(s)</>}
          </Button>
        </>
      )}

      {lastResult && (
        <div className="text-[11px] border rounded p-2 bg-slate-50">
          <div>Applied: <b className="text-emerald-700">{lastResult.applied}</b> · Unchanged: <b className="text-slate-700">{lastResult.unchanged}</b> · Not found: <b className="text-rose-700">{lastResult.not_found}</b></div>
          {lastResult.results?.filter(r => r.status === 'corrected').slice(0, 5).map((r, i) => (
            <div key={i} className="text-[10px] mt-1">
              {r.entity_kind.toUpperCase()} {r.entity_number} — {r.lines_changed} line(s) updated
            </div>
          ))}
        </div>
      )}

      {rows.length === 0 && !loading && (
        <p className="text-xs text-muted-foreground">Click Audit to scan dispatch challans / manifests for incorrect customer DC# / PO#. Only items with a recorded trace_lot can be auto-corrected.</p>
      )}
    </div>
  );
}


function ReopenPrematureClosedPOsCard() {
  const [customerId, setCustomerId] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [reopening, setReopening] = React.useState(false);
  const [flagged, setFlagged] = React.useState([]);
  const [scannedCount, setScannedCount] = React.useState(null);
  const [selected, setSelected] = React.useState({});
  const [audited, setAudited] = React.useState(false);

  const runAudit = async () => {
    setLoading(true);
    setAudited(false);
    setFlagged([]);
    setSelected({});
    try {
      const params = {};
      if (customerId.trim()) params.customer_id = customerId.trim();
      const res = await dispatchAPI.auditPrematurelyClosedPOs(params);
      const list = res.data?.flagged || [];
      setFlagged(list);
      setScannedCount(res.data?.scanned || 0);
      setAudited(true);
      const sel = {};
      list.forEach(p => { sel[p.po_id] = true; });
      setSelected(sel);
      if (list.length === 0) {
        toast.info(`Scanned ${res.data?.scanned || 0} auto-closed POs — no prematurely-closed POs found.`);
      } else {
        toast.success(`${list.length} prematurely-closed PO(s) found out of ${res.data?.scanned || 0} scanned.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Audit failed');
    } finally {
      setLoading(false);
    }
  };

  const toggle = (poId) => setSelected(s => ({ ...s, [poId]: !s[poId] }));
  const toggleAll = () => {
    const allOn = flagged.every(p => selected[p.po_id]);
    const next = {};
    flagged.forEach(p => { next[p.po_id] = !allOn; });
    setSelected(next);
  };

  const reopen = async () => {
    const ids = Object.keys(selected).filter(k => selected[k]);
    if (ids.length === 0) { toast.error('Select at least one PO to re-open'); return; }
    if (!window.confirm(`Re-open ${ids.length} prematurely-closed PO(s)? This restores each PO's pre-close status (usually "active") and clears the auto-close audit fields.`)) return;
    setReopening(true);
    try {
      const res = await dispatchAPI.reopenPrematurelyClosedPOs({
        dry_run: false,
        customer_id: customerId.trim() || undefined,
        po_ids: ids,
      });
      toast.success(`Re-opened ${res.data?.reopened_count || 0} PO(s).`);
      await runAudit();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Re-open failed');
    } finally {
      setReopening(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-[240px]">
          <Label className="text-xs">Customer ID (optional)</Label>
          <Input
            value={customerId}
            onChange={e => setCustomerId(e.target.value)}
            placeholder="Leave blank to scan all customers"
            className="h-8 text-xs"
            data-testid="reopen-pos-customer-input"
          />
        </div>
        <Button onClick={runAudit} disabled={loading} className="h-8 text-xs" data-testid="reopen-pos-audit-btn">
          {loading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5 mr-1" />}
          {loading ? 'Scanning…' : 'Run Audit (Dry-Run)'}
        </Button>
        {audited && flagged.length > 0 && (
          <>
            <Button onClick={toggleAll} variant="outline" className="h-8 text-xs" data-testid="reopen-pos-toggle-all">
              {flagged.every(p => selected[p.po_id]) ? 'Unselect all' : 'Select all'}
            </Button>
            <Button onClick={reopen} disabled={reopening} className="h-8 text-xs bg-amber-600 hover:bg-amber-700" data-testid="reopen-pos-apply-btn">
              {reopening ? 'Re-opening…' : `Re-open ${Object.values(selected).filter(Boolean).length} Selected`}
            </Button>
          </>
        )}
      </div>

      {audited && (
        <p className="text-xs text-muted-foreground" data-testid="reopen-pos-summary">
          Scanned {scannedCount} auto-closed POs. Flagged as prematurely closed: <b>{flagged.length}</b>.
        </p>
      )}

      {flagged.length > 0 && (
        <div className="border rounded-md overflow-hidden max-h-[520px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-amber-50 sticky top-0 z-10">
              <tr>
                <th className="p-2 w-8"></th>
                <th className="p-2 text-left">PO #</th>
                <th className="p-2 text-left">Customer</th>
                <th className="p-2 text-left">Closed At</th>
                <th className="p-2 text-left">Shortfall (per item)</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {flagged.map(p => (
                <tr key={p.po_id} className="hover:bg-amber-50/50" data-testid={`reopen-pos-row-${p.po_number}`}>
                  <td className="p-2"><Checkbox checked={!!selected[p.po_id]} onCheckedChange={() => toggle(p.po_id)} /></td>
                  <td className="p-2 font-mono">{p.po_number}</td>
                  <td className="p-2">{p.customer_name}</td>
                  <td className="p-2 text-[10px]">{(p.closed_at || '').slice(0, 10)}</td>
                  <td className="p-2">
                    <div className="flex flex-col gap-0.5">
                      {p.items_gap.filter(g => g.is_short).map(g => (
                        <span key={g.item_id} className="text-[10px] text-amber-800">
                          {g.item_name || g.item_id}: dispatched <b>{g.dispatched_qty}</b> / ordered <b>{g.ordered_qty}</b> · short <b>{g.shortfall}</b>
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}



function CancelManifestCard() {
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [manifests, setManifests] = useState([]);
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState('');
  const [deleteCocs, setDeleteCocs] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const load = async () => {
    setLoading(true);
    setLastResult(null);
    try {
      const params = { limit: 50 };
      if (search.trim()) params.search = search.trim();
      const res = await dispatchAPI.listCancellableManifests(params);
      setManifests(res.data.manifests || []);
      setSelected(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load manifests');
    } finally { setLoading(false); }
  };

  const submit = async () => {
    if (!selected) { toast.error('Select a manifest first'); return; }
    if (!reason.trim()) { toast.error('Cancellation reason is required'); return; }
    if (!window.confirm(
      `Cancel manifest ${selected.manifest_number}?\n` +
      `· ${selected.linked_dcs.length} dispatch challan(s) will be marked cancelled\n` +
      `· Stock will be restored to source godown(s)\n` +
      `· Trace lot quantities will be reversed\n` +
      (deleteCocs ? '· Attached COCs will be removed\n' : '') +
      `Reason: ${reason}\nThis action is audit-logged. Continue?`
    )) return;
    setSubmitting(true);
    try {
      const res = await dispatchAPI.cancelManifest(selected.id, {
        reason: reason.trim(),
        delete_cocs: deleteCocs,
      });
      setLastResult(res.data);
      toast.success(`Cancelled. ${res.data.dcs_cancelled} DC(s) reversed.`);
      setReason('');
      setDeleteCocs(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cancellation failed');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs">Search (Manifest # / Customer)</Label>
          <Input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()}
            placeholder="e.g. ABCENGINEE-MF-000005 or TATA"
            className="h-8 text-xs" data-testid="cancel-manifest-search" />
        </div>
        <Button size="sm" onClick={load} disabled={loading} className="h-8" data-testid="cancel-manifest-load-btn">
          {loading ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Loading...</> : <><Search className="w-3 h-3 mr-1" /> Load</>}
        </Button>
      </div>

      {manifests.length > 0 && (
        <div className="border rounded-md overflow-auto max-h-[360px]">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="p-2 w-8"></th>
                <th className="p-2 text-left font-medium">Manifest #</th>
                <th className="p-2 text-left font-medium">Customer</th>
                <th className="p-2 text-left font-medium">Status</th>
                <th className="p-2 text-left font-medium">Items / Qty</th>
                <th className="p-2 text-left font-medium">Linked DCs</th>
              </tr>
            </thead>
            <tbody>
              {manifests.map(mf => {
                const checked = selected?.id === mf.id;
                return (
                  <tr
                    key={mf.id}
                    className={`border-t hover:bg-slate-50/50 ${!mf.is_cancellable ? 'opacity-50' : 'cursor-pointer'} ${checked ? 'bg-rose-50/60' : ''}`}
                    onClick={() => mf.is_cancellable && setSelected(mf)}
                  >
                    <td className="p-2 text-center">
                      <input
                        type="radio"
                        name="cancel-manifest"
                        checked={checked}
                        disabled={!mf.is_cancellable}
                        onChange={() => setSelected(mf)}
                        data-testid={`cancel-mf-radio-${mf.manifest_number}`}
                      />
                    </td>
                    <td className="p-2 font-mono text-[11px]">{mf.manifest_number}</td>
                    <td className="p-2 text-[11px]">{mf.customer_name || '—'}</td>
                    <td className="p-2"><Badge variant="outline" className="text-[10px]">{mf.status}</Badge></td>
                    <td className="p-2 text-[11px]">{mf.items_count} · {mf.total_quantity}</td>
                    <td className="p-2 text-[11px]">
                      {mf.linked_dcs.map(d => (
                        <div key={d.id} className="flex items-center gap-1">
                          <code className="text-[10px]">{d.dc_number}</code>
                          {d.reconciliation_status === 'reconciled' && (
                            <Badge className="text-[9px] bg-rose-100 text-rose-700">reconciled</Badge>
                          )}
                          {d.status === 'cancelled' && (
                            <Badge variant="outline" className="text-[9px]">already cancelled</Badge>
                          )}
                        </div>
                      ))}
                      {!mf.is_cancellable && (
                        <p className="text-[10px] text-rose-700 mt-0.5">Un-reconcile DC(s) before cancelling</p>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className="border rounded-md p-3 bg-rose-50/40 space-y-2">
          <div className="text-[12px] font-medium">
            Cancel <code className="text-rose-700">{selected.manifest_number}</code> for <b>{selected.customer_name || 'this customer'}</b>
          </div>
          <Textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Reason for cancellation (required, audit-logged)"
            className="text-xs min-h-[60px]"
            data-testid="cancel-manifest-reason"
          />
          <label className="flex items-center gap-1.5 text-[11px] cursor-pointer">
            <input type="checkbox" checked={deleteCocs} onChange={e => setDeleteCocs(e.target.checked)} data-testid="cancel-manifest-delete-cocs" />
            Also delete attached COC files on linked DCs
          </label>
          <Button
            size="sm"
            variant="destructive"
            onClick={submit}
            disabled={submitting || !reason.trim()}
            data-testid="cancel-manifest-submit"
          >
            {submitting ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Cancelling...</> : <><AlertTriangle className="w-3 h-3 mr-1" /> Cancel manifest & restore stock</>}
          </Button>
        </div>
      )}

      {lastResult && (
        <div className="text-[11px] border rounded p-2 bg-slate-50">
          <div>Manifest <b>{lastResult.manifest_number}</b> cancelled. DCs reversed: <b>{lastResult.dcs_cancelled}</b>{lastResult.cocs_deleted ? ` · COCs removed: ${lastResult.cocs_deleted}` : ''}</div>
          {(lastResult.reversal_summary || []).map((d, i) => (
            <div key={i} className="text-[10px] mt-1 border-t pt-1">
              <code>{d.dc_number}</code>: {(d.lines || []).map((ln, j) => (
                <span key={j} className="ml-1">
                  {ln.item_unique_id || ln.item_id?.slice(0, 8)} +{ln.qty_restored}
                  {ln.trace_residual_unallocated > 0 && <span className="text-amber-700"> (residual {ln.trace_residual_unallocated} unmapped)</span>}
                </span>
              ))}
            </div>
          ))}
        </div>
      )}

      {manifests.length === 0 && !loading && (
        <p className="text-xs text-muted-foreground">Click Load to find manifests. Only manifests whose DCs are not yet reconciled with an invoice can be cancelled here.</p>
      )}
    </div>
  );
}


function CancelProductionReceiptCard() {
  const [woNumber, setWoNumber] = useState('');
  const [woId, setWoId] = useState('');
  const [loading, setLoading] = useState(false);
  const [receipts, setReceipts] = useState([]);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const lookupWO = async () => {
    if (!woNumber.trim()) { toast.error('Enter a WO number'); return; }
    setLoading(true);
    setLastResult(null);
    setReceipts([]);
    setSelectedReceipt(null);
    try {
      // First find the WO by number to get its id
      const woRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/inventory/work-orders?search=${encodeURIComponent(woNumber.trim())}&limit=5`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const woJson = await woRes.json();
      const wo = (woJson.work_orders || []).find(w => w.wo_number?.toUpperCase() === woNumber.trim().toUpperCase());
      if (!wo) { toast.error(`WO ${woNumber} not found`); setLoading(false); return; }
      setWoId(wo.id);
      const res = await inventoryAPI.listWoReceipts(wo.id);
      setReceipts(res.data.receipts || []);
      if ((res.data.receipts || []).length === 0) toast.info('No production receipts found for this WO');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Lookup failed');
    } finally { setLoading(false); }
  };

  const submit = async () => {
    if (!selectedReceipt) { toast.error('Select a receipt first'); return; }
    if (!reason.trim()) { toast.error('Reason required'); return; }
    if (!window.confirm(
      `Cancel this production receipt?\n` +
      `· ${selectedReceipt.trace_lots.length} FG trace lot(s) will be deleted\n` +
      `· ${selectedReceipt.consumed_materials.length} BOM consumption(s) will be reversed (RM returned to source)\n` +
      `· FG inventory will decrease by ${selectedReceipt.produced_quantity}\n` +
      `· WO produced_quantity will decrease by ${selectedReceipt.produced_quantity}\n` +
      `Reason: ${reason}\nThis is audit-logged. Continue?`
    )) return;
    setSubmitting(true);
    try {
      const res = await inventoryAPI.cancelProductionReceipt(selectedReceipt.txn_id, { reason: reason.trim() });
      setLastResult(res.data);
      toast.success(`Receipt cancelled. ${res.data.trace_lots_deleted} trace_lots removed, ${res.data.rm_restored.length} RM lines restored.`);
      setReason('');
      // Reload receipts
      if (woId) {
        const r = await inventoryAPI.listWoReceipts(woId);
        setReceipts(r.data.receipts || []);
        setSelectedReceipt(null);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cancellation failed');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs">Work Order #</Label>
          <Input value={woNumber} onChange={e => setWoNumber(e.target.value)} onKeyDown={e => e.key === 'Enter' && lookupWO()}
            placeholder="e.g. WO-000048" className="h-8 text-xs" data-testid="cancel-receipt-wo-input" />
        </div>
        <Button size="sm" onClick={lookupWO} disabled={loading} className="h-8" data-testid="cancel-receipt-lookup-btn">
          {loading ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Loading...</> : <><Search className="w-3 h-3 mr-1" /> Find receipts</>}
        </Button>
      </div>

      {receipts.length > 0 && (
        <div className="border rounded-md overflow-auto max-h-[400px]">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="p-2 w-8"></th>
                <th className="p-2 text-left font-medium">Date</th>
                <th className="p-2 text-left font-medium">Qty</th>
                <th className="p-2 text-left font-medium">Trace Lots</th>
                <th className="p-2 text-left font-medium">Consumed</th>
                <th className="p-2 text-left font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {receipts.map(r => {
                const checked = selectedReceipt?.txn_id === r.txn_id;
                return (
                  <tr key={r.txn_id} className={`border-t hover:bg-slate-50/50 ${!r.is_cancellable ? 'opacity-50' : 'cursor-pointer'} ${checked ? 'bg-rose-50/60' : ''}`}
                      onClick={() => r.is_cancellable && setSelectedReceipt(r)}>
                    <td className="p-2 text-center">
                      <input type="radio" name="cancel-receipt" checked={checked} disabled={!r.is_cancellable} onChange={() => setSelectedReceipt(r)} />
                    </td>
                    <td className="p-2 text-[11px]">{r.created_at?.slice(0, 16).replace('T', ' ')}</td>
                    <td className="p-2 text-[11px]">{r.produced_quantity}</td>
                    <td className="p-2 text-[11px]">
                      {r.trace_lots.map((tl, i) => (
                        <div key={i}>
                          <code className="text-[10px]">{tl.trace_id || tl.id?.slice(0, 6)}</code>
                          <span className="text-muted-foreground"> avail={tl.available_quantity}</span>
                          {tl.dispatched_quantity > 0 && <Badge className="text-[9px] ml-1 bg-rose-100 text-rose-700">dispatched</Badge>}
                        </div>
                      ))}
                    </td>
                    <td className="p-2 text-[11px]">{r.consumed_materials.length} line(s)</td>
                    <td className="p-2">
                      {r.status === 'cancelled' && <Badge variant="outline" className="text-[10px]">cancelled</Badge>}
                      {r.any_trace_lot_dispatched && <Badge className="text-[9px] bg-amber-100 text-amber-700">already dispatched</Badge>}
                      {r.is_cancellable && <Badge className="text-[10px] bg-emerald-100 text-emerald-700">cancellable</Badge>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selectedReceipt && (
        <div className="border rounded-md p-3 bg-rose-50/40 space-y-2">
          <Textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason (required, audit-logged)" className="text-xs min-h-[50px]" data-testid="cancel-receipt-reason" />
          <Button size="sm" variant="destructive" onClick={submit} disabled={submitting || !reason.trim()} data-testid="cancel-receipt-submit">
            {submitting ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Cancelling...</> : <><AlertTriangle className="w-3 h-3 mr-1" /> Cancel receipt &amp; restore everything</>}
          </Button>
        </div>
      )}

      {lastResult && (
        <div className="text-[11px] border rounded p-2 bg-slate-50">
          <div>WO: <code>{lastResult.wo_number}</code> · trace_lots removed: <b>{lastResult.trace_lots_deleted}</b> · FG decremented: <b>{lastResult.fg_decremented}</b></div>
          <div className="text-[10px] mt-1">RM restored: {lastResult.rm_restored?.map((r, i) => <span key={i} className="ml-1">{r.item_name} +{r.qty}</span>)}</div>
        </div>
      )}

      {receipts.length === 0 && !loading && (
        <p className="text-xs text-muted-foreground">Look up a WO to see its production receipts. Cancelling a receipt deletes its FG trace lots, restores RM stock to STORE, and decrements the WO's produced_quantity.</p>
      )}
    </div>
  );
}


function ManifestStockDebugCard() {
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const load = async () => {
    if (!search.trim()) { toast.error('Enter an item unique id'); return; }
    setLoading(true);
    try {
      const res = await inventoryAPI.manifestStockDebug({ item_unique_id: search.trim() });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
      setData(null);
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[220px]">
          <Label className="text-xs">Item Unique ID</Label>
          <Input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()}
            placeholder="e.g. HINGE-AFT-RH" className="h-8 text-xs" data-testid="manifest-debug-input" />
        </div>
        <Button size="sm" onClick={load} disabled={loading} className="h-8" data-testid="manifest-debug-btn">
          {loading ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Loading...</> : <><Search className="w-3 h-3 mr-1" /> Debug</>}
        </Button>
      </div>

      {data && (
        <div className="space-y-3 text-xs">
          <div className="font-medium">{data.item_unique_id} — {data.item_name}</div>

          <div className="border rounded p-2">
            <div className="font-medium mb-1 text-[11px]">Per-godown summary (what manifest will offer)</div>
            <table className="w-full text-[11px]">
              <thead className="text-muted-foreground">
                <tr>
                  <th className="text-left font-normal">Godown</th>
                  <th className="text-right font-normal">Balance</th>
                  <th className="text-right font-normal">Σ Trace lots</th>
                  <th className="text-right font-normal">Drift</th>
                  <th className="text-right font-normal">Manifest offers</th>
                </tr>
              </thead>
              <tbody>
                {(data.derived_summary || []).map((s, i) => (
                  <tr key={i} className="border-t">
                    <td className="py-1">{s.godown_name}</td>
                    <td className="text-right font-mono">{s.balance_quantity}</td>
                    <td className="text-right font-mono">{s.trace_lots_total_available}</td>
                    <td className="text-right">
                      {s.drift !== 0 && <Badge className={s.drift > 0 ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}>{s.drift > 0 ? '+' : ''}{s.drift}</Badge>}
                    </td>
                    <td className="text-right font-mono"><b>{s.manifest_will_offer}</b></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <details className="border rounded p-2" open>
            <summary className="cursor-pointer text-[11px] font-medium">Trace lots ({data.trace_lots.length})</summary>
            <table className="w-full text-[10px] mt-2">
              <thead className="text-muted-foreground">
                <tr>
                  <th className="text-left font-normal">Trace ID</th>
                  <th className="text-left font-normal">Godown</th>
                  <th className="text-right font-normal">Avail</th>
                  <th className="text-right font-normal">Disp</th>
                  <th className="text-left font-normal">WO</th>
                  <th className="text-left font-normal">Cust WO</th>
                  <th className="text-left font-normal">Source IC</th>
                  <th className="text-left font-normal">IC Status</th>
                  <th className="text-left font-normal">Manifest scope?</th>
                  <th className="text-left font-normal">Lot Status</th>
                </tr>
              </thead>
              <tbody>
                {data.trace_lots.map(tl => (
                  <tr key={tl.id} className={`border-t ${tl.available_quantity > 0 && tl.source_inbound_in_manifest_scope === false ? 'bg-red-50' : ''}`}>
                    <td className="py-1"><code>{tl.trace_id?.slice(0, 30) || tl.id?.slice(0, 8)}</code></td>
                    <td>{tl.godown_name}</td>
                    <td className="text-right font-mono">{tl.available_quantity}</td>
                    <td className="text-right font-mono">{tl.dispatched_quantity}</td>
                    <td>{tl.wo_number || '—'}</td>
                    <td>{tl.customer_wo_number || '—'}</td>
                    <td>{tl.source_inbound_challan_number || (tl.inbound_challan_id ? <span className="text-amber-700">(id only)</span> : '—')}</td>
                    <td>{tl.source_inbound_status || '—'}</td>
                    <td>
                      {tl.inbound_challan_id ? (
                        tl.source_inbound_in_manifest_scope
                          ? <Badge className="bg-emerald-100 text-emerald-700 text-[9px]">in scope</Badge>
                          : <Badge className="bg-red-100 text-red-700 text-[9px]">ORPHAN (out of scope)</Badge>
                      ) : <Badge variant="outline" className="text-[9px]">no IC link</Badge>}
                    </td>
                    <td>{tl.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[10px] text-muted-foreground mt-2">
              <b>Reading this:</b> A trace lot is offered by the manifest builder only when (a) it has a non-zero{' '}
              <code>available_quantity</code>, (b) its source inbound challan exists, (c) that challan's status is{' '}
              <code>sent</code>, <code>partially_received</code> or <code>received</code> ("in scope"), and (d) the
              challan's <code>expected_output_items</code> declares this FG. Rows highlighted in red are physically
              available but excluded because the source IC is out of scope (typically <code>closed</code>) — that's
              an orphan condition the auto-populator currently skips.
            </p>
          </details>

          <details className="border rounded p-2">
            <summary className="cursor-pointer text-[11px] font-medium">Inbound challans declaring this FG ({data.inbound_challans_with_expected_outputs.length})</summary>
            <pre className="text-[10px] mt-2 bg-slate-50 p-2 overflow-auto max-h-[200px]">{JSON.stringify(data.inbound_challans_with_expected_outputs, null, 2)}</pre>
          </details>

          <details className="border rounded p-2">
            <summary className="cursor-pointer text-[11px] font-medium">Non-cancelled dispatch challan items ({data.open_dispatch_challans.length})</summary>
            <pre className="text-[10px] mt-2 bg-slate-50 p-2 overflow-auto max-h-[200px]">{JSON.stringify(data.open_dispatch_challans, null, 2)}</pre>
          </details>
        </div>
      )}
    </div>
  );
}





function ReconcileSubcontractChallanCard() {
  const [challanId, setChallanId] = useState('');
  const [reason, setReason] = useState('Admin reconcile: credit challan from misrouted receive txns');
  const [scanning, setScanning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [plan, setPlan] = useState(null);

  const runScan = async () => {
    if (!challanId.trim()) { toast.error('Enter a challan ID'); return; }
    setScanning(true);
    setPlan(null);
    try {
      const res = await inventoryAPI.reconcileSubcontractChallan({
        challan_id: challanId.trim(),
        apply: false,
        reason: reason.trim() || 'Admin reconcile',
      });
      setPlan(res.data);
      if ((res.data.credits_planned || 0) === 0) toast.info('No misrouted receipts found for this challan.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed');
    } finally { setScanning(false); }
  };

  const runApply = async () => {
    if (!plan || (plan.credits_planned || 0) === 0) return;
    if (!window.confirm(
      `Credit ${plan.credits_planned} inventory transaction(s) to challan ${plan.challan_number}? ` +
      `Total qty credited: ${plan.total_quantity_credited}. ` +
      `Inventory itself is NOT changed (those txns already updated it). Proceed?`
    )) return;
    setApplying(true);
    try {
      const res = await inventoryAPI.reconcileSubcontractChallan({
        challan_id: challanId.trim(),
        apply: true,
        reason: reason.trim() || 'Admin reconcile',
      });
      toast.success(`Credited ${res.data.applied} txn(s); challan status → ${res.data.new_status}`);
      setPlan(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Apply failed');
    } finally { setApplying(false); }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
        <div>
          <Label className="text-[11px]">Subcontract Challan ID (UUID)</Label>
          <Input value={challanId} onChange={e => setChallanId(e.target.value)} placeholder="Paste challan id from URL or DB" className="h-8 text-xs" data-testid="reconcile-subcon-id" />
        </div>
        <div className="md:col-span-2">
          <Label className="text-[11px]">Reason (audited)</Label>
          <Input value={reason} onChange={e => setReason(e.target.value)} className="h-8 text-xs" data-testid="reconcile-subcon-reason" />
        </div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={runScan} disabled={scanning} className="h-8" data-testid="reconcile-subcon-scan-btn">
          {scanning ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Scanning...</> : <><Search className="w-3 h-3 mr-1" /> Scan (dry-run)</>}
        </Button>
        {plan && (plan.credits_planned || 0) > 0 && !plan.applied && (
          <Button size="sm" variant="destructive" onClick={runApply} disabled={applying} className="h-8" data-testid="reconcile-subcon-apply-btn">
            {applying ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Applying...</> : <>Apply</>}
          </Button>
        )}
      </div>
      {plan && (
        <div className="border rounded p-2 space-y-2 bg-slate-50/60 text-[11px]">
          <div className="flex flex-wrap gap-3">
            <div>Challan: <b>{plan.challan_number}</b></div>
            <div>Current status: <b>{plan.current_status}</b></div>
            <div>Candidate txns: <b>{plan.candidate_txns}</b></div>
            <div>Credits planned: <b>{plan.credits_planned}</b></div>
            <div>Qty credited: <b className="font-mono">{plan.total_quantity_credited}</b></div>
            {plan.new_status && <Badge className="bg-emerald-100 text-emerald-700">New status: {plan.new_status}</Badge>}
          </div>
          {(plan.proposed || []).length > 0 && (
            <details open={(plan.proposed || []).length <= 8}>
              <summary className="cursor-pointer font-medium">Per-txn breakdown ({plan.proposed.length})</summary>
              <table className="w-full text-[10px] mt-1.5">
                <thead className="text-muted-foreground">
                  <tr>
                    <th className="text-left font-normal">Txn ID</th>
                    <th className="text-left font-normal">Item</th>
                    <th className="text-left font-normal">Ref</th>
                    <th className="text-right font-normal">Txn Qty</th>
                    <th className="text-right font-normal">Credit Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.proposed.map((r, i) => (
                    <tr key={i} className="border-t" data-testid={`reconcile-subcon-row-${i}`}>
                      <td className="py-1"><code>{(r.txn_id || '').slice(0, 8)}</code></td>
                      <td>{r.item_name}</td>
                      <td>{r.reference_number}</td>
                      <td className="text-right font-mono">{r.txn_quantity}</td>
                      <td className="text-right font-mono">{r.credit_quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </div>
      )}
    </div>
  );
}


function BackfillReceiptQcCard() {
  const [scanning, setScanning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [plan, setPlan] = useState(null);

  const runScan = async () => {
    setScanning(true);
    setPlan(null);
    try {
      const res = await inventoryAPI.backfillReceiptQc({ apply: false });
      setPlan(res.data);
      if ((res.data.to_backfill || 0) === 0) toast.info('No historical receipts need backfilling.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed');
    } finally { setScanning(false); }
  };

  const runApply = async () => {
    if (!plan || (plan.to_backfill || 0) === 0) return;
    if (!window.confirm(
      `Backfill qc_required + qc_status on ${plan.to_backfill} historical receive transaction(s)?\n\n` +
      `Each touched row gains: qc_required=true, qc_status="pending_qc", qty counters reset, ` +
      `pending_qc_qty = quantity. Audited.\n\nProceed?`
    )) return;
    setApplying(true);
    try {
      const res = await inventoryAPI.backfillReceiptQc({ apply: true });
      toast.success(`Backfilled ${res.data.applied} row(s)`);
      setPlan(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Apply failed');
    } finally { setApplying(false); }
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Button size="sm" onClick={runScan} disabled={scanning} className="h-8" data-testid="backfill-qc-scan-btn">
          {scanning ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Scanning...</> : <><Search className="w-3 h-3 mr-1" /> Scan (dry-run)</>}
        </Button>
        {plan && (plan.to_backfill || 0) > 0 && !plan.applied && (
          <Button size="sm" variant="destructive" onClick={runApply} disabled={applying} className="h-8" data-testid="backfill-qc-apply-btn">
            {applying ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Applying...</> : <>Apply backfill on {plan.to_backfill} row(s)</>}
          </Button>
        )}
      </div>

      {plan && (
        <div className="border rounded p-2 space-y-2 bg-slate-50/60 text-[11px]">
          <div className="flex flex-wrap gap-3">
            <div>Scanned: <b>{plan.scanned}</b></div>
            <div>To backfill: <b>{plan.to_backfill}</b></div>
            {plan.applied !== undefined && plan.applied > 0 && (
              <Badge className="bg-emerald-100 text-emerald-700">Applied: {plan.applied}</Badge>
            )}
          </div>
          {(plan.fixes || []).length > 0 && (
            <details open={(plan.fixes || []).length <= 8}>
              <summary className="cursor-pointer font-medium">Per-row breakdown ({plan.fixes.length})</summary>
              <table className="w-full text-[10px] mt-1.5">
                <thead className="text-muted-foreground">
                  <tr>
                    <th className="text-left font-normal">Date</th>
                    <th className="text-left font-normal">Item</th>
                    <th className="text-right font-normal">Qty</th>
                    <th className="text-left font-normal">Reference</th>
                    <th className="text-left font-normal">Supplier</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.fixes.map((r, i) => (
                    <tr key={i} className="border-t" data-testid={`backfill-qc-row-${i}`}>
                      <td className="py-1">{(r.date || '').slice(0, 10)}</td>
                      <td>{r.item_unique_id || r.item_name?.slice(0, 32)}</td>
                      <td className="text-right font-mono">{r.quantity}</td>
                      <td>{r.reference_number}</td>
                      <td>{r.supplier_name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </div>
      )}
    </div>
  );
}


function AutoFixStaleFgLotsCard() {
  const [itemUniqueId, setItemUniqueId] = useState('');
  const [reason, setReason] = useState('Auto-fix: stale lot zeroed to reconcile FG drift');
  const [onlyFg, setOnlyFg] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [plan, setPlan] = useState(null);

  const runScan = async () => {
    setScanning(true);
    setPlan(null);
    try {
      const res = await inventoryAPI.autoFixStaleFgLots({
        apply: false,
        reason: reason.trim() || 'Auto-fix: stale lot zeroed to reconcile FG drift',
        item_unique_id: itemUniqueId.trim() || null,
        only_finished_goods: onlyFg,
      });
      setPlan(res.data);
      if ((res.data.rows_with_drift || 0) === 0) toast.info('No high drift detected — nothing to fix.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed');
    } finally { setScanning(false); }
  };

  const runApply = async () => {
    if (!plan || (plan.rows_with_drift || 0) === 0) { toast.error('Run a scan first'); return; }
    if (!reason.trim()) { toast.error('Reason is required'); return; }
    if (!window.confirm(
      `Apply ${plan.lots_to_edit} lot edit(s) across ${plan.rows_with_drift} (item, godown) row(s)?\n\n` +
      `Total units removed from available: ${plan.total_units_to_remove}\n` +
      `Each edit is audited in trace_lot_admin_edits.\n\nProceed?`
    )) return;
    setApplying(true);
    try {
      const res = await inventoryAPI.autoFixStaleFgLots({
        apply: true,
        reason: reason.trim(),
        item_unique_id: itemUniqueId.trim() || null,
        only_finished_goods: onlyFg,
      });
      toast.success(`Applied ${res.data.applied_edits} lot edit(s)`);
      setPlan(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Apply failed');
    } finally { setApplying(false); }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
        <div>
          <Label className="text-[11px]">Item Unique ID (optional — leave blank to scan all items)</Label>
          <Input value={itemUniqueId} onChange={e => setItemUniqueId(e.target.value)} placeholder="e.g. 149A7631-607" className="h-8 text-xs" data-testid="auto-fix-stale-fg-item" />
        </div>
        <div>
          <Label className="text-[11px]">Reason (required when applying)</Label>
          <Input value={reason} onChange={e => setReason(e.target.value)} className="h-8 text-xs" data-testid="auto-fix-stale-fg-reason" />
        </div>
        <div className="flex items-center gap-2 pb-2">
          <input id="only-fg" type="checkbox" checked={onlyFg} onChange={e => setOnlyFg(e.target.checked)} data-testid="auto-fix-stale-fg-only-fg" />
          <Label htmlFor="only-fg" className="text-[11px] cursor-pointer">Restrict to FG / FINISHED godowns only</Label>
        </div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={runScan} disabled={scanning} className="h-8" data-testid="auto-fix-stale-fg-scan-btn">
          {scanning ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Scanning...</> : <><Search className="w-3 h-3 mr-1" /> Scan (dry-run)</>}
        </Button>
        {plan && (plan.rows_with_drift || 0) > 0 && !plan.applied_edits && (
          <Button size="sm" variant="destructive" onClick={runApply} disabled={applying} className="h-8" data-testid="auto-fix-stale-fg-apply-btn">
            {applying ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Applying...</> : <>Apply {plan.lots_to_edit} edit(s)</>}
          </Button>
        )}
      </div>

      {plan && (
        <div className="border rounded p-2 space-y-2 bg-slate-50/60 text-[11px]">
          <div className="flex flex-wrap gap-3">
            <div>Balance rows scanned: <b>{plan.scanned}</b></div>
            <div>Rows with drift: <b>{plan.rows_with_drift}</b></div>
            <div>Lots to edit: <b>{plan.lots_to_edit}</b></div>
            <div>Units to remove: <b className="font-mono">{plan.total_units_to_remove}</b></div>
            {plan.applied_edits !== undefined && (
              <Badge className="bg-emerald-100 text-emerald-700">Applied: {plan.applied_edits}</Badge>
            )}
          </div>
          {(plan.fixes || []).length > 0 && (
            <details open={(plan.fixes || []).length <= 5}>
              <summary className="cursor-pointer font-medium">Per-row breakdown ({plan.fixes.length})</summary>
              <table className="w-full text-[10px] mt-1.5">
                <thead className="text-muted-foreground">
                  <tr>
                    <th className="text-left font-normal">Item</th>
                    <th className="text-left font-normal">Godown</th>
                    <th className="text-right font-normal">Balance</th>
                    <th className="text-right font-normal">Σtl</th>
                    <th className="text-right font-normal">Drift</th>
                    <th className="text-right font-normal">Remove</th>
                    <th className="text-left font-normal">Affected lots</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.fixes.map((r, i) => (
                    <tr key={i} className="border-t" data-testid={`auto-fix-row-${i}`}>
                      <td className="py-1">{r.item_unique_id || r.item_id?.slice(0,8)} — {r.item_name}</td>
                      <td>{r.godown_name}</td>
                      <td className="text-right font-mono">{r.balance_quantity}</td>
                      <td className="text-right font-mono">{r.trace_lots_total_available}</td>
                      <td className="text-right font-mono text-red-700">{r.drift}</td>
                      <td className="text-right font-mono">{r.to_remove}</td>
                      <td>
                        {(r.lot_edits || []).map((e, j) => (
                          <div key={j} className="text-[9px] leading-tight">
                            <code>{(e.trace_id || e.trace_lot_id || '').toString().slice(0,32)}</code>
                            {e.wo_number ? ` · WO ${e.wo_number}` : ''}
                            {e.customer_wo_number ? ` · cwo ${e.customer_wo_number}` : ''}
                            : avail {e.before.available_quantity} → {e.after.available_quantity} (−{e.removed})
                          </div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </div>
      )}
    </div>
  );
}


function EditTraceLotCard() {
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [matches, setMatches] = useState([]);
  const [picked, setPicked] = useState(null);
  const [newQty, setNewQty] = useState('');
  const [newAvail, setNewAvail] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const runSearch = async () => {
    if (!search.trim()) { toast.error('Enter a trace_id or trace lot UUID'); return; }
    setLoading(true);
    setPicked(null);
    setLastResult(null);
    try {
      const res = await inventoryAPI.traceLotSearch({ q: search.trim(), limit: 25 });
      const rows = res.data.matches || [];
      setMatches(rows);
      if (rows.length === 0) toast.info('No trace lots matched.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Search failed');
      setMatches([]);
    } finally { setLoading(false); }
  };

  const pick = (tl) => {
    setPicked(tl);
    setNewQty(String(tl.quantity ?? ''));
    setNewAvail(String(tl.available_quantity ?? ''));
    setReason('');
    setLastResult(null);
  };

  const save = async () => {
    if (!picked) return;
    if (!reason.trim()) { toast.error('Reason is required'); return; }
    const q = parseFloat(newQty);
    const a = parseFloat(newAvail);
    if (Number.isNaN(q) || Number.isNaN(a)) { toast.error('Enter valid numbers for both quantities'); return; }
    if (q < 0 || a < 0) { toast.error('Quantities cannot be negative'); return; }
    if (a > q + 1e-9) { toast.error('available_quantity cannot exceed quantity'); return; }
    if (!window.confirm(
      `Override trace_lot:\n${picked.trace_id || picked.id}\n\n` +
      `quantity: ${picked.quantity} → ${q}\n` +
      `available_quantity: ${picked.available_quantity} → ${a}\n\n` +
      `inventory_balances will NOT be touched (use Trace Drift reconcile separately if needed). Continue?`
    )) return;
    setSaving(true);
    try {
      const res = await inventoryAPI.editTraceLot({
        trace_lot_id: picked.id,
        new_quantity: q,
        new_available_quantity: a,
        reason: reason.trim(),
      });
      setLastResult(res.data);
      toast.success('Trace lot updated');
      const updated = {
        ...picked,
        quantity: res.data.after.quantity,
        available_quantity: res.data.after.available_quantity,
        status: res.data.after.status,
      };
      setPicked(updated);
      setMatches(prev => prev.map(m => m.id === picked.id ? updated : m));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[260px]">
          <Label className="text-xs">Trace ID or Trace Lot UUID</Label>
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && runSearch()}
            placeholder="e.g. ABC-DC-000037-#1 / WO-000035  OR  3a5983ea-460f-..."
            className="h-8 text-xs"
            data-testid="edit-trace-lot-search-input"
          />
        </div>
        <Button size="sm" onClick={runSearch} disabled={loading} className="h-8" data-testid="edit-trace-lot-search-btn">
          {loading ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Searching...</> : <><Search className="w-3 h-3 mr-1" /> Search</>}
        </Button>
      </div>

      {matches.length > 0 && (
        <div className="border rounded">
          <table className="w-full text-[11px]">
            <thead className="text-muted-foreground bg-slate-50">
              <tr>
                <th className="text-left font-normal p-1.5">Trace ID</th>
                <th className="text-left font-normal p-1.5">Item</th>
                <th className="text-left font-normal p-1.5">Godown</th>
                <th className="text-right font-normal p-1.5">Qty</th>
                <th className="text-right font-normal p-1.5">Avail</th>
                <th className="text-right font-normal p-1.5">Disp</th>
                <th className="text-left font-normal p-1.5">Status</th>
                <th className="p-1.5"></th>
              </tr>
            </thead>
            <tbody>
              {matches.map(tl => (
                <tr key={tl.id} className={`border-t ${picked?.id === tl.id ? 'bg-blue-50' : ''}`}>
                  <td className="p-1.5"><code className="text-[10px]">{tl.trace_id || tl.id}</code></td>
                  <td className="p-1.5">{tl.item_unique_id} <span className="text-muted-foreground">— {tl.item_name}</span></td>
                  <td className="p-1.5">{tl.godown_name}</td>
                  <td className="p-1.5 text-right font-mono">{tl.quantity}</td>
                  <td className="p-1.5 text-right font-mono">{tl.available_quantity}</td>
                  <td className="p-1.5 text-right font-mono">{tl.dispatched_quantity}</td>
                  <td className="p-1.5"><Badge variant="outline" className="text-[9px]">{tl.status}</Badge></td>
                  <td className="p-1.5 text-right">
                    <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => pick(tl)} data-testid={`edit-trace-lot-pick-${tl.id}`}>Edit</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {picked && (
        <div className="border rounded p-3 space-y-2 bg-amber-50/40">
          <div className="text-xs font-medium">
            Editing: <code className="text-[10px]">{picked.trace_id || picked.id}</code>
            <span className="text-muted-foreground"> · {picked.item_unique_id} · {picked.godown_name}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div>
              <Label className="text-[11px]">New quantity</Label>
              <Input value={newQty} onChange={e => setNewQty(e.target.value)} type="number" step="any" className="h-8 text-xs" data-testid="edit-trace-lot-new-qty" />
            </div>
            <div>
              <Label className="text-[11px]">New available_quantity</Label>
              <Input value={newAvail} onChange={e => setNewAvail(e.target.value)} type="number" step="any" className="h-8 text-xs" data-testid="edit-trace-lot-new-avail" />
            </div>
            <div className="text-[11px] text-muted-foreground self-end pb-2">
              dispatched_quantity stays at <b>{picked.dispatched_quantity}</b>
            </div>
          </div>
          <div>
            <Label className="text-[11px]">Reason (required, audited)</Label>
            <Textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} className="text-xs" placeholder="Why is this manual override necessary?" data-testid="edit-trace-lot-reason" />
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={save} disabled={saving} className="h-8" data-testid="edit-trace-lot-save-btn">
              {saving ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Saving...</> : <>Save Override</>}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setPicked(null); setLastResult(null); }} className="h-8" data-testid="edit-trace-lot-cancel-btn">
              Cancel
            </Button>
          </div>
          {lastResult && (
            <div className="text-[11px] border rounded p-2 bg-emerald-50">
              <div className="font-medium mb-1">Updated successfully</div>
              <div>quantity: <code>{lastResult.before.quantity}</code> → <code>{lastResult.after.quantity}</code></div>
              <div>available_quantity: <code>{lastResult.before.available_quantity}</code> → <code>{lastResult.after.available_quantity}</code></div>
              <div>status: <code>{lastResult.before.status}</code> → <code>{lastResult.after.status}</code></div>
            </div>
          )}
        </div>
      )}

      {matches.length === 0 && !loading && !picked && (
        <p className="text-[11px] text-muted-foreground">
          Search by full or partial trace_id (case-insensitive) or by the trace lot's UUID.
          Pick a row to override its <code>quantity</code> and <code>available_quantity</code>. The
          inventory_balance row is intentionally not touched — use the
          Inventory ↔ Trace Lot Drift card afterwards if you also need to fix the godown total.
        </p>
      )}
    </div>
  );
}




function InventoryTraceDriftCard() {
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { only_drifted: true };
      if (search.trim()) params.item_unique_id = search.trim();
      const res = await inventoryAPI.inventoryTraceDrift(params);
      setRows(res.data.rows || []);
      if ((res.data.rows || []).length === 0) toast.info('No drift detected.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setLoading(false); }
  };

  const reconcile = async (row, target) => {
    const reason = window.prompt(
      `Reconcile ${row.item_unique_id} @ ${row.godown_name}\n\n` +
      `Balance: ${row.balance_quantity}\nTrace lots sum: ${row.trace_lots_total_available}\nDrift: ${row.drift}\n\n` +
      (target === 'trace_lots'
        ? 'This will set balance.quantity = trace_lots_total. Reason for audit log:'
        : 'This will scale trace_lots so they total balance.quantity. Reason for audit log:')
    );
    if (!reason || !reason.trim()) return;
    setSubmitting(true);
    try {
      await inventoryAPI.reconcileInventoryTraceDrift({
        item_id: row.item_id,
        godown_id: row.godown_id,
        target,
        reason: reason.trim(),
      });
      toast.success('Reconciled');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs">Item Unique ID (optional)</Label>
          <Input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()}
            placeholder="e.g. HINGE-AFT-RH" className="h-8 text-xs" data-testid="drift-search" />
        </div>
        <Button size="sm" onClick={load} disabled={loading} className="h-8" data-testid="drift-load-btn">
          {loading ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Scanning...</> : <><Search className="w-3 h-3 mr-1" /> Scan</>}
        </Button>
      </div>

      {rows.length > 0 && (
        <div className="border rounded-md overflow-auto max-h-[440px]">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="p-2 text-left font-medium">Item</th>
                <th className="p-2 text-left font-medium">Godown</th>
                <th className="p-2 text-right font-medium">Balance</th>
                <th className="p-2 text-right font-medium">Trace Lots Σ</th>
                <th className="p-2 text-right font-medium">Drift</th>
                <th className="p-2 text-left font-medium">Trace Lots</th>
                <th className="p-2 text-left font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className="border-t hover:bg-slate-50/50">
                  <td className="p-2 text-[11px]"><div className="font-medium">{r.item_unique_id}</div><div className="text-muted-foreground text-[10px]">{r.item_name}</div></td>
                  <td className="p-2 text-[11px]">{r.godown_name}</td>
                  <td className="p-2 text-right text-[11px] font-mono">{r.balance_quantity}</td>
                  <td className="p-2 text-right text-[11px] font-mono">{r.trace_lots_total_available}</td>
                  <td className="p-2 text-right text-[11px] font-mono">
                    <Badge className={r.drift > 0 ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}>{r.drift > 0 ? '+' : ''}{r.drift}</Badge>
                  </td>
                  <td className="p-2 text-[10px]">
                    {r.trace_lots.slice(0, 5).map((tl, j) => (
                      <div key={j}><code>{tl.trace_id?.slice(0, 16) || tl.id?.slice(0, 8)}</code> avail={tl.available_quantity}</div>
                    ))}
                    {r.trace_lots.length > 5 && <div className="text-muted-foreground">+{r.trace_lots.length - 5} more</div>}
                  </td>
                  <td className="p-2 space-y-1">
                    <Button size="sm" variant="outline" className="h-6 text-[10px] w-full" disabled={submitting}
                      onClick={() => reconcile(r, 'trace_lots')}>
                      Use trace lot total
                    </Button>
                    <Button size="sm" variant="outline" className="h-6 text-[10px] w-full" disabled={submitting}
                      onClick={() => reconcile(r, 'balance')}>
                      Use balance (scale lots)
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rows.length === 0 && !loading && (
        <p className="text-xs text-muted-foreground">Click Scan to find rows where inventory_balance ≠ Σ trace_lots.available_quantity.</p>
      )}
    </div>
  );
}



function StaleFgTraceLotCleanupCard() {
  const [loading, setLoading] = useState(false);
  const [lots, setLots] = useState([]);
  const [selected, setSelected] = useState({});
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const load = async () => {
    setLoading(true);
    setLastResult(null);
    try {
      const res = await inventoryAPI.listStaleFgTraceLots({});
      const data = res.data.trace_lots || [];
      setLots(data);
      if (data.length === 0) toast.info('No FG trace lots found.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setLoading(false); }
  };

  const submit = async () => {
    const ids = Object.keys(selected).filter(k => selected[k]);
    if (!ids.length) { toast.error('Select at least one trace lot'); return; }
    if (!reason.trim()) { toast.error('Reason required'); return; }
    if (!window.confirm(`Delete ${ids.length} trace lot(s)? This decrements FG inventory by their available_quantity. Audit-logged.`)) return;
    setSubmitting(true);
    try {
      const res = await inventoryAPI.deleteTraceLots({ trace_lot_ids: ids, reason: reason.trim() });
      setLastResult(res.data);
      toast.success(`Deleted ${res.data.deleted.length}. Skipped ${res.data.skipped.length}.`);
      setSelected({});
      setReason('');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-3">
      <Button size="sm" onClick={load} disabled={loading} className="h-8" data-testid="stale-tl-load-btn">
        {loading ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Loading...</> : <><Search className="w-3 h-3 mr-1" /> Load FG trace lots</>}
      </Button>

      {lots.length > 0 && (
        <>
          <div className="border rounded-md overflow-auto max-h-[400px]">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="p-2 w-8"></th>
                  <th className="p-2 text-left font-medium">Trace ID</th>
                  <th className="p-2 text-left font-medium">Item</th>
                  <th className="p-2 text-left font-medium">WO</th>
                  <th className="p-2 text-left font-medium">Avail / Dispatched</th>
                  <th className="p-2 text-left font-medium">Receipt</th>
                </tr>
              </thead>
              <tbody>
                {lots.map(tl => (
                  <tr key={tl.id} className={`border-t hover:bg-slate-50/50 ${tl.is_orphan ? 'bg-amber-50/40' : ''}`}>
                    <td className="p-2 text-center">
                      <input type="checkbox"
                        checked={!!selected[tl.id]}
                        disabled={tl.dispatched_quantity > 0}
                        onChange={() => setSelected(prev => ({ ...prev, [tl.id]: !prev[tl.id] }))}
                      />
                    </td>
                    <td className="p-2 font-mono text-[10px]">{tl.trace_id || tl.id?.slice(0, 8)}</td>
                    <td className="p-2 text-[11px]">{tl.item_unique_id || tl.item_id?.slice(0, 8)}</td>
                    <td className="p-2 text-[11px]">{tl.wo_number || '—'}</td>
                    <td className="p-2 text-[11px]">{tl.available_quantity} / {tl.dispatched_quantity}</td>
                    <td className="p-2">
                      {tl.is_orphan
                        ? <Badge className="text-[9px] bg-amber-100 text-amber-700">orphan</Badge>
                        : <Badge variant="outline" className="text-[9px]">{tl.receipt_status || 'active'}</Badge>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="space-y-2">
            <Textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason (required)" className="text-xs min-h-[40px]" />
            <Button size="sm" variant="destructive" onClick={submit} disabled={submitting} data-testid="stale-tl-delete-btn">
              {submitting ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Deleting...</> : <><Trash2 className="w-3 h-3 mr-1" /> Delete {Object.values(selected).filter(Boolean).length} selected</>}
            </Button>
          </div>
        </>
      )}

      {lastResult && (
        <div className="text-[11px] border rounded p-2 bg-slate-50">
          Deleted: {lastResult.deleted.length} · Skipped: {lastResult.skipped.length}
          {lastResult.skipped.length > 0 && (
            <div className="text-[10px] text-amber-700 mt-1">
              Skipped reasons: {lastResult.skipped.map((s, i) => <span key={i}> {s.reason}</span>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


export default function AdminToolsPage() {
  const navigate = useNavigate();
  const [seedingFixedAssets, setSeedingFixedAssets] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const [transferring, setTransferring] = useState(false);
  const [transferResult, setTransferResult] = useState(null);
  const [repairing, setRepairing] = useState(false);
  const [repairResult, setRepairResult] = useState(null);

  // Update Traceability
  const [traceSearch, setTraceSearch] = useState('');
  const [traceSearching, setTraceSearching] = useState(false);
  const [traceChallan, setTraceChallan] = useState(null);
  const [selectedLineIdx, setSelectedLineIdx] = useState(null);
  const [newTraceId, setNewTraceId] = useState('');
  const [traceRemarks, setTraceRemarks] = useState('');
  const [updatingTrace, setUpdatingTrace] = useState(false);
  const [traceResult, setTraceResult] = useState(null);

  // Update WO Challan Qty
  const [woSearch, setWoSearch] = useState('');
  const [woSearching, setWoSearching] = useState(false);
  const [woDetail, setWoDetail] = useState(null);
  const [selectedCliIdx, setSelectedCliIdx] = useState(null);
  const [newCliQty, setNewCliQty] = useState('');
  const [woQtyRemarks, setWoQtyRemarks] = useState('');
  const [updatingWoQty, setUpdatingWoQty] = useState(false);
  const [woQtyResult, setWoQtyResult] = useState(null);

  const searchChallanForTrace = async () => {
    if (!traceSearch.trim()) { toast.error('Enter a challan number'); return; }
    setTraceSearching(true);
    setTraceChallan(null);
    setSelectedLineIdx(null);
    setTraceResult(null);
    try {
      const res = await subcontractAPI.listChallans({ search: traceSearch.trim(), limit: 1, order_type: 'inbound' });
      const challans = res.data.challans || [];
      if (challans.length === 0) {
        toast.error('No inbound challan found');
        return;
      }
      // Get full challan detail
      const detail = await subcontractAPI.getChallan(challans[0].id);
      setTraceChallan(detail.data);
    } catch { toast.error('Failed to search challan'); }
    finally { setTraceSearching(false); }
  };

  const handleUpdateTrace = async () => {
    if (selectedLineIdx === null || !traceChallan) return;
    if (!newTraceId.trim()) { toast.error('Enter the new traceability ID'); return; }
    setUpdatingTrace(true);
    setTraceResult(null);
    try {
      const res = await api.patch(`/subcontract/challans/${traceChallan.id}/update-traceability`, {
        line_index: selectedLineIdx,
        new_traceability_id: newTraceId.trim(),
        remarks: traceRemarks.trim(),
      });
      setTraceResult(res.data);
      toast.success(res.data.message);
      const detail = await subcontractAPI.getChallan(traceChallan.id);
      setTraceChallan(detail.data);
      setSelectedLineIdx(null);
      setNewTraceId('');
      setTraceRemarks('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update traceability');
    } finally { setUpdatingTrace(false); }
  };

  // WO Challan Qty handlers
  const searchWoForQtyFix = async () => {
    if (!woSearch.trim()) { toast.error('Enter a work order number'); return; }
    setWoSearching(true);
    setWoDetail(null);
    setSelectedCliIdx(null);
    setWoQtyResult(null);
    try {
      const res = await api.get('/inventory/work-orders', { params: { search: woSearch.trim(), limit: 1 } });
      const wos = res.data.work_orders || [];
      if (wos.length === 0) { toast.error('Work order not found'); return; }
      setWoDetail(wos[0]);
    } catch { toast.error('Failed to search work order'); }
    finally { setWoSearching(false); }
  };

  const handleUpdateWoQty = async () => {
    if (selectedCliIdx === null || !woDetail) return;
    const cli = woDetail.challan_line_items[selectedCliIdx];
    const qty = parseFloat(newCliQty);
    if (isNaN(qty) || qty < 0) { toast.error('Enter a valid quantity'); return; }
    setUpdatingWoQty(true);
    setWoQtyResult(null);
    try {
      const res = await api.patch(`/inventory/work-orders/${woDetail.id}/update-challan-qty`, {
        challan_id: cli.challan_id,
        line_index: cli.line_index,
        new_selected_quantity: qty,
        remarks: woQtyRemarks.trim(),
      });
      setWoQtyResult(res.data);
      toast.success(res.data.message);
      // Refresh WO detail
      const refreshRes = await api.get('/inventory/work-orders', { params: { search: woDetail.wo_number, limit: 1 } });
      if (refreshRes.data.work_orders?.length > 0) setWoDetail(refreshRes.data.work_orders[0]);
      setSelectedCliIdx(null);
      setNewCliQty('');
      setWoQtyRemarks('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally { setUpdatingWoQty(false); }
  };

  const runFixedAssetSeeding = async () => {
    if (!window.confirm('This will add inventory stock of 1 for all Fixed Assets and Calibration items. Continue?')) {
      return;
    }
    
    setSeedingFixedAssets(true);
    setSeedResult(null);
    try {
      const res = await api.post('/inventory/admin/seed-fixed-asset-inventory');
      setSeedResult(res.data);
      toast.success(`Seeding complete: ${res.data.added} added, ${res.data.skipped} skipped`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to run seeding');
      setSeedResult({ error: err.response?.data?.detail || 'Failed' });
    } finally {
      setSeedingFixedAssets(false);
    }
  };

  const runBulkTransferToQC = async () => {
    if (!window.confirm('This will transfer all Fixed Asset / Calibration item stock from STORE to Quality Room. Continue?')) {
      return;
    }
    
    setTransferring(true);
    setTransferResult(null);
    try {
      const res = await api.post('/inventory/admin/transfer-assets-to-quality-room');
      setTransferResult(res.data);
      toast.success(`Transfer complete: ${res.data.transferred} items moved to ${res.data.to_godown}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to run transfer');
      setTransferResult({ error: err.response?.data?.detail || 'Failed' });
    } finally {
      setTransferring(false);
    }
  };

  const runRepairStuckChallans = async () => {
    if (!window.confirm('This will scan all challans with short discrepancies and fix expected quantities so they become closable. Continue?')) {
      return;
    }
    
    setRepairing(true);
    setRepairResult(null);
    try {
      const res = await api.post('/subcontract/repair-stuck-challans');
      setRepairResult(res.data);
      if (res.data.patched_count > 0) {
        toast.success(`Repaired ${res.data.patched_count} stuck challans`);
      } else {
        toast.info('No stuck challans found — all are already closable');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to run repair');
      setRepairResult({ error: err.response?.data?.detail || 'Failed' });
    } finally {
      setRepairing(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Wrench className="w-6 h-6 text-slate-600" />
          Admin Tools
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          One-time setup and maintenance operations
        </p>
      </div>

      <div className="grid gap-4">
        {/* Email configuration */}
        <Card data-testid="email-settings-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Mail className="w-5 h-5 text-indigo-600" />
              Email Settings
            </CardTitle>
            <CardDescription>
              Default email IDs, reply-to addresses, a master send switch and on/off switches for every scheduled alert
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button size="sm" onClick={() => navigate('/admin/email-settings')} data-testid="open-email-settings-btn">
              <Mail className="w-3.5 h-3.5 mr-1" /> Open Email Settings
            </Button>
          </CardContent>
        </Card>

        {/* Fixed Asset Inventory Seeding */}
        <Card data-testid="seed-fixed-assets-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-600" />
              Seed Fixed Asset Inventory
            </CardTitle>
            <CardDescription>
              Add opening stock of 1 unit for all Fixed Assets and Calibration items in STORE godown
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                  <div className="text-xs text-amber-800">
                    <p className="font-medium">Safe to run multiple times</p>
                    <p>Items that already have stock ≥ 1 will be skipped. Creates audit trail transactions.</p>
                  </div>
                </div>
              </div>

              <Button 
                onClick={runFixedAssetSeeding} 
                disabled={seedingFixedAssets}
                className="w-full sm:w-auto"
                data-testid="run-seed-fixed-assets-btn"
              >
                {seedingFixedAssets ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Running...
                  </>
                ) : (
                  <>
                    <Database className="w-4 h-4 mr-2" />
                    Run Fixed Asset Seeding
                  </>
                )}
              </Button>

              {/* Results */}
              {seedResult && !seedResult.error && (
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="seed-result">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    <span className="font-medium text-emerald-800">Seeding Complete</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="text-center p-2 bg-white rounded border">
                      <div className="text-xl font-bold text-slate-700">{seedResult.items_processed}</div>
                      <div className="text-[10px] text-muted-foreground">Items Processed</div>
                    </div>
                    <div className="text-center p-2 bg-white rounded border border-emerald-200">
                      <div className="text-xl font-bold text-emerald-600">{seedResult.added}</div>
                      <div className="text-[10px] text-emerald-600">Added</div>
                    </div>
                    <div className="text-center p-2 bg-white rounded border border-blue-200">
                      <div className="text-xl font-bold text-blue-600">{seedResult.updated}</div>
                      <div className="text-[10px] text-blue-600">Updated</div>
                    </div>
                    <div className="text-center p-2 bg-white rounded border border-slate-200">
                      <div className="text-xl font-bold text-slate-500">{seedResult.skipped}</div>
                      <div className="text-[10px] text-muted-foreground">Skipped</div>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Godown: <Badge variant="outline" className="text-[10px]">{seedResult.godown}</Badge>
                  </p>
                </div>
              )}

              {seedResult?.error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  Error: {seedResult.error}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        {/* Bulk Transfer to Quality Room */}
        <Card data-testid="transfer-to-qc-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-teal-600" />
              Transfer Assets to Quality Room
            </CardTitle>
            <CardDescription>
              Bulk transfer all Fixed Asset and Calibration item stock from STORE to Quality Room godown
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-blue-600 mt-0.5" />
                  <div className="text-xs text-blue-800">
                    <p className="font-medium">Safe to run multiple times</p>
                    <p>Items already in Quality Room with sufficient stock will be skipped. Creates full audit trail.</p>
                  </div>
                </div>
              </div>

              <Button 
                onClick={runBulkTransferToQC} 
                disabled={transferring}
                className="w-full sm:w-auto"
                data-testid="run-transfer-to-qc-btn"
              >
                {transferring ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Transferring...
                  </>
                ) : (
                  <>
                    <ArrowRightLeft className="w-4 h-4 mr-2" />
                    Transfer to Quality Room
                  </>
                )}
              </Button>

              {/* Transfer Results */}
              {transferResult && !transferResult.error && (
                <div className="p-4 bg-teal-50 border border-teal-200 rounded-lg" data-testid="transfer-result">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle2 className="w-5 h-5 text-teal-600" />
                    <span className="font-medium text-teal-800">Transfer Complete</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="text-center p-2 bg-white rounded border">
                      <div className="text-xl font-bold text-slate-700">{transferResult.items_found}</div>
                      <div className="text-[10px] text-muted-foreground">Items Found</div>
                    </div>
                    <div className="text-center p-2 bg-white rounded border border-teal-200">
                      <div className="text-xl font-bold text-teal-600">{transferResult.transferred}</div>
                      <div className="text-[10px] text-teal-600">Transferred</div>
                    </div>
                    <div className="text-center p-2 bg-white rounded border border-slate-200">
                      <div className="text-xl font-bold text-slate-500">{transferResult.skipped_no_stock}</div>
                      <div className="text-[10px] text-muted-foreground">No Stock</div>
                    </div>
                    <div className="text-center p-2 bg-white rounded border border-slate-200">
                      <div className="text-xl font-bold text-slate-500">{transferResult.skipped_already_in_qc}</div>
                      <div className="text-[10px] text-muted-foreground">Already in QC</div>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    From <Badge variant="outline" className="text-[10px]">{transferResult.from_godown}</Badge> to <Badge variant="outline" className="text-[10px] border-teal-300 text-teal-700">{transferResult.to_godown}</Badge>
                  </p>
                </div>
              )}

              {transferResult?.error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  Error: {transferResult.error}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Repair Stuck Challans */}
        <Card data-testid="repair-stuck-challans-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Wrench className="w-5 h-5 text-amber-600" />
              Repair Stuck Challans
            </CardTitle>
            <CardDescription>
              Fix challans where short discrepancies were reported but expected qty wasn't updated (old bug), making them closable again
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                  <div className="text-xs text-amber-800">
                    <p className="font-medium">Safe to run multiple times</p>
                    <p>Only patches challans where expected qty &gt; received qty due to unpatched short discrepancies. Already-closable challans are skipped.</p>
                  </div>
                </div>
              </div>

              <Button 
                onClick={runRepairStuckChallans} 
                disabled={repairing}
                className="w-full sm:w-auto"
                data-testid="run-repair-stuck-challans-btn"
              >
                {repairing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Scanning & Repairing...
                  </>
                ) : (
                  <>
                    <Wrench className="w-4 h-4 mr-2" />
                    Repair Stuck Challans
                  </>
                )}
              </Button>

              {repairResult && !repairResult.error && (
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="repair-result">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                    <span className="font-medium text-emerald-800">
                      {repairResult.patched_count > 0 ? 'Repair Complete' : 'No Stuck Challans Found'}
                    </span>
                  </div>
                  <div className="text-center p-2 bg-white rounded border border-emerald-200 inline-block">
                    <div className="text-xl font-bold text-emerald-600">{repairResult.patched_count}</div>
                    <div className="text-[10px] text-emerald-600">Challans Patched</div>
                  </div>
                  {repairResult.patched_challans?.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {repairResult.patched_challans.map((c, i) => (
                        <div key={i} className="text-xs flex items-center gap-2">
                          <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                          <span className="font-mono">{c.challan_number}</span>
                          <Badge variant="outline" className="text-[10px]">{c.status}</Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {repairResult?.error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  Error: {repairResult.error}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Fix Legacy Receipt QC */}
        <Card data-testid="backfill-mrb-trace-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <PackageCheck className="w-5 h-5 text-purple-600" />
              Backfill MRB Trace Lots
            </CardTitle>
            <CardDescription>
              Repairs missing trace lots on legacy quarantine → rejection MRB cases so dispatch lines populate trace_id and customer WO.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <BackfillMrbTraceLotsCard />
          </CardContent>
        </Card>

        <Card data-testid="backfill-instruments-item-master-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Wrench className="w-5 h-5 text-teal-600" />
              Instruments → Item Master
            </CardTitle>
            <CardDescription>
              Backfill instruments created before the Item Master rule: creates a matching Fixed Asset (Calibration Required) for each unlinked instrument and links it.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <BackfillInstrumentItemMasterCard />
          </CardContent>
        </Card>

        <Card data-testid="migrate-invoices-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileInput className="w-5 h-5 text-blue-600" />
              Migrate Invoices → Persistent Storage
            </CardTitle>
            <CardDescription>
              One-shot backfill of legacy dispatch invoices from local disk into Emergent Object Storage.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <MigrateInvoicesCard />
          </CardContent>
        </Card>

        <Card data-testid="heal-stale-indents-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <PackageCheck className="w-5 h-5 text-emerald-600" />
              Heal Stale Indents (unblock Auto-Indent)
            </CardTitle>
            <CardDescription>
              Unblocks auto-indent for items whose previous PO was already received/closed but whose indent record is still stuck in <code>po_sent</code>.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <HealStaleIndentsCard />
          </CardContent>
        </Card>

        <Card data-testid="fix-receipt-qc-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-blue-600" />
              Fix Legacy Receipt QC
            </CardTitle>
            <CardDescription>
              Search by item code to find receipt transactions missing QC flags, then fix individually.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ReceiptQCFixer />
          </CardContent>
        </Card>

        {/* Bulk Backfill Customer DC# / PO# */}
        <Card data-testid="backfill-customer-refs-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <FileInput className="w-5 h-5 text-indigo-600" />
              Backfill Customer DC# / PO#
            </CardTitle>
            <CardDescription>
              Find legacy inbound sub-contract challans missing the Customer Delivery Challan # or Customer PO #, and fill them in.
              Values propagate automatically to linked manifests & delivery challans.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CustomerRefBackfill />
          </CardContent>
        </Card>

        {/* Audit + reopen prematurely-closed Customer POs (duplicate-line bug fix, Session 27) */}
        <Card data-testid="reopen-premature-closed-pos-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-amber-600" />
              Re-open Prematurely-Closed Customer POs
            </CardTitle>
            <CardDescription>
              Audit auto-closed customer POs whose dispatched qty is still
              short of the (correctly aggregated) ordered qty — caused by the
              pre-Session-27 duplicate-line bug. Dry-run lists candidates per
              item with shortfall; one click reverts them to their pre-close
              status. Manually-closed POs are skipped.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ReopenPrematureClosedPOsCard />
          </CardContent>
        </Card>

        {/* Correct Customer DC# / PO# on Manifests / DCs (uses trace_lot.inbound_challan_id) */}
        <Card data-testid="correct-manifest-dc-customer-refs-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
              Correct Customer DC# / PO# on Manifests &amp; Dispatch Challans
            </CardTitle>
            <CardDescription>
              Re-resolve the customer Delivery Challan # and customer PO # on each line of an existing manifest /
              dispatch challan from the actual trace_lot's source inbound sub-contract challan. Useful for entries
              created before the customer-PO linkage fix when the same finished good came from multiple inbound
              challans. COCs already issued are not regenerated automatically — re-issue them from the dispatch
              page after correction so they pick up the corrected line-level fields.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ManifestDcCustomerRefCorrector />
          </CardContent>
        </Card>

        {/* Cancel a dispatch manifest (admin only) — restores stock + reverses linked DCs */}
        <Card data-testid="cancel-manifest-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-rose-600" />
              Cancel Dispatch Manifest (Admin)
            </CardTitle>
            <CardDescription>
              Reverse a manifest created in error. Restores stock to the source godown(s),
              decrements the trace lots' dispatched quantity / restores their available
              quantity, and marks the manifest plus all linked dispatch challans as
              cancelled. Only manifests whose DCs are <b>not yet reconciled with an
              invoice</b> can be cancelled here. Every change is audit-logged with the
              given reason.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CancelManifestCard />
          </CardContent>
        </Card>

        {/* Cancel Production Receipt — fully reverses a wrong receipt */}
        <Card data-testid="cancel-production-receipt-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-rose-600" />
              Cancel Production Receipt (Admin)
            </CardTitle>
            <CardDescription>
              Reverse a production receipt entered in error. Deletes the FG
              trace_lots created by the receipt, restores the consumed RM
              back to its source godown, decrements the WO's
              produced_quantity, and decrements FG inventory. Cancellation is
              blocked if any of the receipt's FG trace_lots have already
              been dispatched — cancel the dispatch manifest first.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <CancelProductionReceiptCard />
          </CardContent>
        </Card>

        {/* Stale FG trace_lot cleanup */}
        <Card data-testid="stale-fg-trace-lots-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-amber-600" />
              Clean Stale FG Trace Lots (Admin)
            </CardTitle>
            <CardDescription>
              List finished-goods trace_lots in the FG godown. Lots whose
              parent receipt is cancelled are flagged as <Badge variant="outline" className="text-[9px] mx-1">orphan</Badge>.
              Select stale rows to hard-delete them; the FG inventory balance
              is decremented by each deleted lot's available_quantity. Lots
              that have already been dispatched cannot be deleted.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <StaleFgTraceLotCleanupCard />
          </CardContent>
        </Card>

        {/* Inventory ↔ trace_lot drift */}
        <Card data-testid="inventory-trace-drift-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-rose-600" />
              Inventory ↔ Trace Lot Drift (Admin)
            </CardTitle>
            <CardDescription>
              Compare the stock recorded on each (item, godown) row against
              the sum of its trace_lots' available_quantity. If they differ,
              that's why the manifest preview can show a different total
              than the item-master display. Reconcile per-row by either
              matching the balance to the trace_lot total (most common) or
              proportionally scaling the trace_lots to match the balance.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <InventoryTraceDriftCard />
          </CardContent>
        </Card>

        {/* Manifest Stock Debug — drill-down per item */}
        <Card data-testid="manifest-stock-debug-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Search className="w-5 h-5 text-blue-600" />
              Manifest Stock Drill-Down (Admin)
            </CardTitle>
            <CardDescription>
              Investigate exactly why a given item shows the stock it does in
              the manifest preview. Returns: (a) per-godown <i>inventory_balances</i>,
              (b) every <i>trace_lot</i> for the item with its available /
              dispatched quantities and originating WO, (c) the inbound
              sub-contract challans that declared this item as a production
              output (Branch&nbsp;1 source), (d) every non-cancelled dispatch
              challan that lists this item, and (e) the per-godown
              <code className="mx-1 text-[10px]">manifest_will_offer</code>
              after the safety caps are applied. Use this to pinpoint stale
              trace_lots, drift, or wrong customer linkages before correcting.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ManifestStockDebugCard />
          </CardContent>
        </Card>

        {/* Manual Trace Lot Edit (Admin) */}
        <Card data-testid="edit-trace-lot-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Wrench className="w-5 h-5 text-amber-600" />
              Manual Trace Lot Edit (Admin)
            </CardTitle>
            <CardDescription>
              Search a specific <i>trace_lot</i> by its trace ID (or UUID), then manually
              override its <code className="text-[10px]">quantity</code> and
              <code className="mx-1 text-[10px]">available_quantity</code>. This is a
              direct override of what the manifest preview will offer for that lot.
              The matching <i>inventory_balances</i> row is intentionally not touched —
              use the Inventory ↔ Trace Lot Drift card afterwards if you need to
              re-align the godown total. Every edit is audited
              (<code className="text-[10px]">trace_lot_admin_edits</code>) with the
              before/after values, the user, and the reason.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <EditTraceLotCard />
          </CardContent>
        </Card>

        {/* Auto-Fix Stale FG Trace Lots */}
        <Card data-testid="auto-fix-stale-fg-lots-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Wrench className="w-5 h-5 text-rose-600" />
              Auto-Fix Stale FG Trace Lots
            </CardTitle>
            <CardDescription>
              Scans every (item, FG godown) row where <i>Σ trace_lots.available_quantity</i> exceeds the
              <i> inventory_balance</i> — the data signature of "phantom" lots that were never decremented on
              dispatch. The fix picks the <b>oldest available lots first (FIFO)</b> and zeros their
              <code className="mx-1 text-[10px]">available_quantity</code> until alignment is restored,
              so the manifest auto-populator stops silently hiding valid customer WOs. Always run a <b>dry-run</b>
              scan first; the apply phase audits each lot edit in <code className="text-[10px]">trace_lot_admin_edits</code>.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <AutoFixStaleFgLotsCard />
          </CardContent>
        </Card>

        {/* Backfill Receipt QC */}
        <Card data-testid="backfill-receipt-qc-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Wrench className="w-5 h-5 text-sky-600" />
              Backfill Receipt QC (one-shot)
            </CardTitle>
            <CardDescription>
              Historical receipts where the item has <code className="text-[10px]">perform_qc_on_receipt=True</code>
              landed correctly in <i>Inbound Staging</i> but their <code className="text-[10px]">qc_required</code> /
              <code className="text-[10px]">qc_status</code> fields were never persisted (the root cause is fixed in
              the latest code). Those rows are therefore invisible on the Receipt QC tab. This tool finds them and
              writes the missing fields so they appear in <b>Pending QC</b> immediately. Always dry-run first.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <BackfillReceiptQcCard />
          </CardContent>
        </Card>

        {/* Reconcile Subcontract Challan from Inventory Transactions */}
        <Card data-testid="reconcile-subcontract-challan-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Wrench className="w-5 h-5 text-violet-600" />
              Reconcile Subcontract Challan from Inventory Txns
            </CardTitle>
            <CardDescription>
              Use when an operator received returning subcontract items via the wrong path
              (Receive/Issue page → linked to the PO number) instead of Sub-Contract → Receive. The
              receive transaction has already updated inventory; this tool credits the same quantity
              onto the outbound subcontract challan's <code className="text-[10px]">received_quantity</code>
              so the challan can be auto-closed. Inventory itself is <b>not</b> touched (no double-counting).
              Idempotent — re-running won't credit the same txn twice. Always dry-run first.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ReconcileSubcontractChallanCard />
          </CardContent>
        </Card>

        {/* Update Challan Traceability */}
        <Card data-testid="update-traceability-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <GitFork className="w-5 h-5 text-purple-600" />
              Update Challan Traceability ID
            </CardTitle>
            <CardDescription>
              Fix or update the traceability ID (customer work order number) on a specific inbound challan item line. Changes propagate to linked work orders and MRB cases.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Search challan */}
              <div>
                <Label className="text-xs">Search Inbound Challan</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    placeholder="Enter challan number..."
                    value={traceSearch}
                    onChange={e => setTraceSearch(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && searchChallanForTrace()}
                    className="h-8 text-sm max-w-sm"
                    data-testid="trace-challan-search"
                  />
                  <Button size="sm" onClick={searchChallanForTrace} disabled={traceSearching} className="h-8" data-testid="trace-challan-search-btn">
                    {traceSearching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3 mr-1" />}
                    Search
                  </Button>
                </div>
              </div>

              {/* Challan items */}
              {traceChallan && (
                <div className="space-y-3">
                  <div className="p-2 bg-purple-50 rounded border border-purple-200">
                    <p className="text-xs font-medium text-purple-800">
                      {traceChallan.challan_number} — {traceChallan.supplier_name || traceChallan.customer_name}
                    </p>
                    <p className="text-[10px] text-purple-600">
                      Status: {traceChallan.status} | Customer DC: {traceChallan.customer_challan_number || '-'}
                    </p>
                  </div>

                  <table className="w-full text-xs border rounded">
                    <thead>
                      <tr className="bg-muted/50 border-b">
                        <th className="text-left p-2 font-medium">#</th>
                        <th className="text-left p-2 font-medium">Item</th>
                        <th className="text-left p-2 font-medium">Current Trace ID</th>
                        <th className="text-center p-2 font-medium">Qty</th>
                        <th className="p-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(traceChallan.items || []).map((item, idx) => (
                        <tr key={idx} className={`border-b ${selectedLineIdx === idx ? 'bg-purple-50' : 'hover:bg-muted/20'}`}>
                          <td className="p-2">{idx + 1}</td>
                          <td className="p-2">
                            <p className="font-medium">{item.item_name}</p>
                            <p className="text-[10px] text-muted-foreground font-mono">{item.item_unique_id}</p>
                          </td>
                          <td className="p-2">
                            <span className="font-mono font-medium text-purple-700">{item.traceability_id || '-'}</span>
                            {item.traceability_type && (
                              <Badge variant="outline" className="ml-1 text-[9px]">{item.traceability_type}</Badge>
                            )}
                          </td>
                          <td className="p-2 text-center">{item.quantity}</td>
                          <td className="p-2 text-right">
                            <Button
                              size="sm" variant={selectedLineIdx === idx ? 'default' : 'outline'}
                              className="h-6 text-[10px]"
                              onClick={() => {
                                setSelectedLineIdx(idx);
                                setNewTraceId(item.traceability_id || '');
                                setTraceRemarks('');
                                setTraceResult(null);
                              }}
                              data-testid={`trace-select-line-${idx}`}
                            >
                              {selectedLineIdx === idx ? 'Selected' : 'Edit'}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Edit form */}
                  {selectedLineIdx !== null && (
                    <div className="p-3 bg-slate-50 rounded-lg border space-y-3">
                      <p className="text-xs font-medium">
                        Editing Line #{selectedLineIdx + 1}: {traceChallan.items[selectedLineIdx]?.item_name}
                      </p>
                      <div>
                        <Label className="text-xs">New Traceability ID (Customer WO#)</Label>
                        <Input
                          value={newTraceId}
                          onChange={e => setNewTraceId(e.target.value)}
                          className="h-8 text-sm mt-1 max-w-sm font-mono"
                          data-testid="trace-new-id-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Remarks (reason for change)</Label>
                        <Textarea
                          value={traceRemarks}
                          onChange={e => setTraceRemarks(e.target.value)}
                          className="text-xs h-16 mt-1 max-w-sm"
                          placeholder="Why is this being changed?"
                          data-testid="trace-remarks-input"
                        />
                      </div>
                      <Button
                        onClick={handleUpdateTrace}
                        disabled={updatingTrace || !newTraceId.trim()}
                        className="bg-purple-600 hover:bg-purple-700"
                        data-testid="trace-update-btn"
                      >
                        {updatingTrace ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <GitFork className="w-4 h-4 mr-2" />}
                        Update Traceability
                      </Button>
                    </div>
                  )}

                  {/* Result */}
                  {traceResult && (
                    <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                        <span className="text-sm font-medium text-emerald-800">{traceResult.message}</span>
                      </div>
                      {traceResult.work_orders_updated > 0 && (
                        <p className="text-xs text-emerald-700 mt-1">
                          Also updated {traceResult.work_orders_updated} linked work order(s)
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Fix WO Challan Allocation */}
        <Card data-testid="fix-wo-challan-qty-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Wrench className="w-5 h-5 text-orange-600" />
              Fix Work Order Challan Allocation
            </CardTitle>
            <CardDescription>
              Fix legacy work orders where the challan line selected_quantity is higher than what was actually consumed. Updates the WO quantity to match.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <Label className="text-xs">Search Work Order</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    placeholder="Enter WO number (e.g. WO-000034)..."
                    value={woSearch}
                    onChange={e => setWoSearch(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && searchWoForQtyFix()}
                    className="h-8 text-sm max-w-sm"
                    data-testid="wo-fix-search"
                  />
                  <Button size="sm" onClick={searchWoForQtyFix} disabled={woSearching} className="h-8" data-testid="wo-fix-search-btn">
                    {woSearching ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3 mr-1" />}
                    Search
                  </Button>
                </div>
              </div>

              {woDetail && (
                <div className="space-y-3">
                  <div className="p-2 bg-orange-50 rounded border border-orange-200">
                    <p className="text-xs font-medium text-orange-800">
                      {woDetail.wo_number} — {woDetail.item_name}
                    </p>
                    <p className="text-[10px] text-orange-600">
                      Status: {woDetail.status} | WO Qty: {woDetail.quantity} | Produced: {woDetail.produced_quantity || 0}
                    </p>
                  </div>

                  {(woDetail.challan_line_items || []).length === 0 ? (
                    <p className="text-xs text-muted-foreground">No challan line items linked to this work order.</p>
                  ) : (
                    <table className="w-full text-xs border rounded">
                      <thead>
                        <tr className="bg-muted/50 border-b">
                          <th className="text-left p-2 font-medium">Challan</th>
                          <th className="text-left p-2 font-medium">Item</th>
                          <th className="text-center p-2 font-medium">Selected Qty</th>
                          <th className="p-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {woDetail.challan_line_items.map((cli, idx) => (
                          <tr key={idx} className={`border-b ${selectedCliIdx === idx ? 'bg-orange-50' : 'hover:bg-muted/20'}`}>
                            <td className="p-2">
                              <p className="font-mono font-medium">{cli.challan_number}</p>
                              <p className="text-[10px] text-muted-foreground">Line #{cli.line_index + 1}</p>
                            </td>
                            <td className="p-2">{cli.item_name}</td>
                            <td className="p-2 text-center font-bold">{cli.selected_quantity ?? cli.quantity}</td>
                            <td className="p-2 text-right">
                              <Button
                                size="sm" variant={selectedCliIdx === idx ? 'default' : 'outline'}
                                className="h-6 text-[10px]"
                                onClick={() => {
                                  setSelectedCliIdx(idx);
                                  setNewCliQty(String(cli.selected_quantity ?? cli.quantity ?? ''));
                                  setWoQtyRemarks('');
                                  setWoQtyResult(null);
                                }}
                                data-testid={`wo-fix-select-${idx}`}
                              >
                                {selectedCliIdx === idx ? 'Selected' : 'Edit'}
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}

                  {selectedCliIdx !== null && woDetail.challan_line_items[selectedCliIdx] && (
                    <div className="p-3 bg-slate-50 rounded-lg border space-y-3">
                      <p className="text-xs font-medium">
                        Editing: {woDetail.challan_line_items[selectedCliIdx].challan_number} Line #{woDetail.challan_line_items[selectedCliIdx].line_index + 1}
                      </p>
                      <div>
                        <Label className="text-xs">New Selected Quantity</Label>
                        <Input
                          type="number"
                          value={newCliQty}
                          onChange={e => setNewCliQty(e.target.value)}
                          className="h-8 text-sm mt-1 max-w-xs font-mono"
                          data-testid="wo-fix-qty-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Remarks (reason for change)</Label>
                        <Textarea
                          value={woQtyRemarks}
                          onChange={e => setWoQtyRemarks(e.target.value)}
                          className="text-xs h-16 mt-1 max-w-sm"
                          placeholder="e.g. Legacy WO had 51 selected but only 10 consumed"
                          data-testid="wo-fix-remarks-input"
                        />
                      </div>
                      <Button
                        onClick={handleUpdateWoQty}
                        disabled={updatingWoQty || !newCliQty}
                        className="bg-orange-600 hover:bg-orange-700"
                        data-testid="wo-fix-update-btn"
                      >
                        {updatingWoQty ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wrench className="w-4 h-4 mr-2" />}
                        Update Allocation
                      </Button>
                    </div>
                  )}

                  {woQtyResult && (
                    <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                        <span className="text-sm font-medium text-emerald-800">{woQtyResult.message}</span>
                      </div>
                      <p className="text-xs text-emerald-700 mt-1">New WO quantity: {woQtyResult.new_wo_quantity}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
